import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Button, Divider, Form, Input, InputNumber, Modal, Popconfirm, Radio, Select,
  Space, Switch, Tabs, Tag, Typography, message,
} from 'antd';
import type { TabsProps } from 'antd';
import {
  Bell, CheckCircle, ChevronDown, ChevronUp, Clock,
  DollarSign, Edit2, Eye, EyeOff, Globe, ImageIcon,
  Mail, MessageSquare, Plus, RefreshCw,
  Save, Trash2, Upload, Users, X,
} from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setAvatarUrl as setAuthAvatarUrl } from '../../store/authSlice';
import axiosClient from '../../api/axiosClient';
import { comingSoonLabel } from '../../utils/comingSoon';
import CashDrawerSettingsPage from '../CashDrawerSettings';
import './AdminSettingsPage.css';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAY_TO_INT: Record<string, number> = { Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6 };
const INT_TO_DAY: Record<number, string> = { 0: 'Sunday', 1: 'Monday', 2: 'Tuesday', 3: 'Wednesday', 4: 'Thursday', 5: 'Friday', 6: 'Saturday' };
const TEMPLATE_VARS = ['{client_name}', '{service}', '{date}', '{time}', '{staff_name}', '{salon_name}'];

const TIME_OPTIONS = (() => {
  const opts: { label: string; value: string }[] = [];
  for (let h = 6; h <= 23; h++) {
    for (const m of [0, 30]) {
      const label = new Date(2000, 0, 1, h, m).toLocaleTimeString('en-US', {
        hour: 'numeric', minute: '2-digit',
      });
      opts.push({ label, value: `${h}:${m.toString().padStart(2, '0')}` });
    }
  }
  return opts;
})();

// ─── Types ────────────────────────────────────────────────────
interface AddOn { id: string; name: string; price: number; }
interface Service {
  id: string; name: string; defaultPrice: number;
  duration: number; addOns: AddOn[];
}

interface DayHours { open: string; close: string; closed: boolean; }
interface BusinessHoliday {
  holiday_id: string;
  holiday_date: string;
  label: string | null;
  is_closed: boolean;
  open_time: string | null;
  close_time: string | null;
}

interface Barber { user_id: string; name: string; email?: string; }

// ─── Staff Management types (wire format matches backend JSON) ────
interface StaffMember {
  user_id: string; name: string; email: string; phone?: string | null;
  role_id: number; role_name: string; avatar_url?: string; disabled?: boolean;
}
interface StaffPermissions {
  can_change_appointment_status: boolean; can_book_own_appointments: boolean;
  can_view_others_calendar: boolean; can_book_others_appointments: boolean;
  can_create_recurring_blocks: boolean; can_create_others_time_blocks: boolean;
  can_change_waitlist: boolean;
  can_access_client_contact_details: boolean; can_view_client_last_names: boolean;
  can_adjust_client_balances: boolean;
  can_view_individual_sales_on_calendar: boolean; can_view_own_sales_list: boolean;
}
interface CommissionTier { up_to: number | null; rate: number; }
interface StaffCompensation {
  service_commission_enabled: boolean;
  service_commission_type: 'basic' | 'sliding';
  service_commission_basic_rate: number;
  service_commission_scale_type: 'zero-based' | 'progressive';
  service_commission_tiers: CommissionTier[];
  product_commission_enabled: boolean;
  product_commission_type: 'basic' | 'sliding';
  product_commission_basic_rate: number;
  product_commission_scale_type: 'zero-based' | 'progressive';
  product_commission_tiers: CommissionTier[];
  hourly_enabled: boolean;
  hourly_rate: number;
  hourly_greater_of: boolean;
  salary_enabled: boolean;
  monthly_salary: number;
}
interface StaffWorkHour {
  day_of_week: number; is_off: boolean; start_time: string; end_time: string;
  lunch_start: string | null; lunch_end: string | null;
}
interface StaffOverride {
  override_date: string; is_off: boolean; start_time: string; end_time: string;
  lunch_start: string | null; lunch_end: string | null;
}
interface StaffServicePrice { service_id: string; price: number; }
interface StaffNotificationSettings {
  email_new_booking: boolean; email_cancellation: boolean; email_reschedule: boolean;
  email_reminder: boolean; email_daily_summary: boolean; email_weekly_report: boolean;
  sms_new_booking: boolean; sms_cancellation: boolean; sms_reschedule: boolean; sms_reminder: boolean;
  reminder_hours_before: number;
}
const DEFAULT_STAFF_NOTIFICATIONS: StaffNotificationSettings = {
  email_new_booking: true, email_cancellation: true, email_reschedule: true,
  email_reminder: true, email_daily_summary: false, email_weekly_report: false,
  sms_new_booking: false, sms_cancellation: false, sms_reschedule: false, sms_reminder: false,
  reminder_hours_before: 2,
};

interface ReminderTiming { id: string; value: number; unit: 'minutes' | 'hours' | 'days'; }
interface NotifSection {
  emailEnabled: boolean; emailSubject: string; emailBody: string;
  smsEnabled: boolean; smsBody: string;
  // Only meaningful for the 'reminders' section — multiple timing offsets + recipient targeting.
  emailTimings?: ReminderTiming[];
  smsTimings?: ReminderTiming[];
  emailRecipients?: 'clients' | 'clients_admins';
  smsRecipients?: 'clients' | 'clients_admins';
}
interface BookingStep {
  id: string; label: string; description: string; visible: boolean; required: boolean;
}

// ─── Initial data ──────────────────────────────────────────────
const INITIAL_SERVICES: Service[] = [
  { id: 's1', name: "Men's Haircut",   defaultPrice: 35, duration: 45,  addOns: [{ id: 'ao1', name: 'Hot Towel Treatment', price: 10 }, { id: 'ao2', name: 'Neck Massage', price: 8 }] },
  { id: 's2', name: "Women's Haircut", defaultPrice: 45, duration: 60,  addOns: [{ id: 'ao3', name: 'Blowout & Style', price: 20 }, { id: 'ao4', name: 'Deep Conditioning', price: 15 }] },
  { id: 's3', name: "Beard Trim",      defaultPrice: 20, duration: 30,  addOns: [] },
  { id: 's4', name: "Hair Coloring",   defaultPrice: 90, duration: 120, addOns: [{ id: 'ao5', name: 'Toning', price: 25 }, { id: 'ao6', name: 'Gloss Treatment', price: 20 }] },
  { id: 's5', name: "Highlights",      defaultPrice: 75, duration: 90,  addOns: [{ id: 'ao7', name: 'Purple Shampoo Treatment', price: 12 }] },
];

const INITIAL_HOURS: { [d: string]: DayHours } = {
  Monday:    { open: '9:00', close: '18:00', closed: false },
  Tuesday:   { open: '9:00', close: '18:00', closed: false },
  Wednesday: { open: '9:00', close: '18:00', closed: false },
  Thursday:  { open: '9:00', close: '18:00', closed: false },
  Friday:    { open: '9:00', close: '18:00', closed: false },
  Saturday:  { open: '10:00', close: '16:00', closed: false },
  Sunday:    { open: '10:00', close: '16:00', closed: true },
};

const INITIAL_STEPS: BookingStep[] = [
  { id: 'service',    label: 'Service Selection',  description: 'Customer picks the service they want',           visible: true, required: true },
  { id: 'staff',      label: 'Staff Selection',    description: 'Customer chooses a specific stylist',            visible: true, required: false },
  { id: 'datetime',   label: 'Date & Time',        description: 'Customer selects their appointment slot',        visible: true, required: true },
  { id: 'addons',     label: 'Add-Ons',            description: 'Offer optional enhancements to the service',     visible: true, required: false },
  { id: 'clientinfo', label: 'Client Information', description: 'Collect name, email, and phone number',          visible: true, required: true },
  { id: 'notes',      label: 'Special Requests',   description: 'Allow customers to leave a note for the stylist',visible: true, required: false },
];

const mkNotif = (subj: string, body: string, sms: string): NotifSection => ({
  emailEnabled: true, emailSubject: subj, emailBody: body, smsEnabled: true, smsBody: sms,
});

const INITIAL_NOTIFS = {
  bookingConfirmation: mkNotif(
    'Your appointment at {salon_name} is confirmed!',
    `Hi {client_name},\n\nYour appointment has been confirmed!\n\nService: {service}\nDate: {date}\nTime: {time}\nStylist: {staff_name}\n\nSee you soon!\n– {salon_name}`,
    `Hi {client_name}! Your {service} at {salon_name} is confirmed for {date} at {time} with {staff_name}. Reply STOP to opt out.`
  ),
  reminders: {
    ...mkNotif(
      'Reminder: your appointment at {salon_name}',
      `Hi {client_name},\n\nJust a reminder about your upcoming appointment:\n\nService: {service}\nDate: {date}\nTime: {time}\nStylist: {staff_name}\n\nSee you soon!\n– {salon_name}`,
      `Reminder: {client_name}, your {service} at {salon_name} is on {date} at {time}. Reply STOP to opt out.`
    ),
    emailTimings: [{ id: 'rt-e1', value: 24, unit: 'hours' }, { id: 'rt-e2', value: 1, unit: 'hours' }] as ReminderTiming[],
    smsTimings: [{ id: 'rt-s1', value: 24, unit: 'hours' }] as ReminderTiming[],
    emailRecipients: 'clients' as const,
    smsRecipients: 'clients' as const,
  },
  cancellation: mkNotif(
    'Your appointment at {salon_name} has been canceled',
    `Hi {client_name},\n\nYour {service} appointment on {date} at {time} has been canceled.\n\nWe'd love to rebook — visit our scheduling page or call us.\n\n– {salon_name}`,
    `Hi {client_name}, your {service} at {salon_name} on {date} has been canceled. Call us to rebook. Reply STOP to opt out.`
  ),
  rescheduling: mkNotif(
    'Your appointment at {salon_name} has been rescheduled',
    `Hi {client_name},\n\nYour appointment has been rescheduled!\n\nNew Date: {date}\nNew Time: {time}\nService: {service}\nStylist: {staff_name}\n\nSee you soon!\n– {salon_name}`,
    `Hi {client_name}, your {service} at {salon_name} has been rescheduled to {date} at {time}. Reply STOP to opt out.`
  ),
};


// ─── Services Tab ─────────────────────────────────────────────
function ServicesTab({ services, setServices }: { services: Service[]; setServices: (s: Service[]) => void }) {
  const [addingAddOnTo, setAddingAddOnTo] = useState<string | null>(null);
  const [addonName, setAddonName] = useState('');
  const [addonPrice, setAddonPrice] = useState('');
  const [editingSvc, setEditingSvc] = useState<Service | null>(null);
  const [addingSvc, setAddingSvc] = useState(false);
  const [form] = Form.useForm();

  function removeService(id: string) {
    Modal.confirm({
      title: 'Delete service?',
      content: 'This cannot be undone.',
      okType: 'danger',
      onOk: async () => {
        try {
          await axiosClient.delete(`/settings/services/${id}`);
          setServices(services.filter(s => s.id !== id));
        } catch {
          message.error('Failed to delete service.');
        }
      },
    });
  }

  async function removeAddon(svcId: string, aoId: string) {
    try {
      await axiosClient.delete(`/settings/services/${svcId}/addons/${aoId}`);
      setServices(services.map(s => s.id === svcId ? { ...s, addOns: s.addOns.filter(a => a.id !== aoId) } : s));
    } catch {
      message.error('Failed to delete add-on.');
    }
  }

  async function saveAddon(svcId: string) {
    if (!addonName.trim()) return;
    try {
      const resp = await axiosClient.post(`/settings/services/${svcId}/addons`, {
        name: addonName.trim(),
        price: parseFloat(addonPrice) || 0,
      });
      setServices(services.map(s => s.id === svcId
        ? { ...s, addOns: [...s.addOns, { id: resp.data.addon_id, name: addonName.trim(), price: parseFloat(addonPrice) || 0 }] }
        : s));
      setAddonName(''); setAddonPrice(''); setAddingAddOnTo(null);
    } catch {
      message.error('Failed to add add-on.');
    }
  }

  function openEditModal(svc: Service) {
    setEditingSvc(svc);
    form.setFieldsValue({ name: svc.name, defaultPrice: svc.defaultPrice, duration: svc.duration });
  }

  function openAddModal() { setAddingSvc(true); form.resetFields(); }

  function handleModalOk() {
    form.validateFields().then(async vals => {
      const payload = {
        name: vals.name,
        category: '',
        duration_min: vals.duration,
        price: vals.defaultPrice,
        description: '',
        active: true,
        sort_order: 0,
      };
      if (editingSvc) {
        try {
          await axiosClient.put(`/settings/services/${editingSvc.id}`, payload);
          setServices(services.map(s => s.id === editingSvc.id ? { ...s, ...vals } : s));
          setEditingSvc(null);
        } catch {
          message.error('Failed to update service.');
        }
      } else {
        try {
          const resp = await axiosClient.post('/settings/services', payload);
          setServices([...services, { id: resp.data.service_id, ...vals, addOns: [] }]);
          setAddingSvc(false);
        } catch {
          message.error('Failed to add service.');
        }
      }
      form.resetFields();
    });
  }

  return (
    <div>
      <div className="tab-top-bar">
        <h2 className="tab-section-title">Services &amp; Add-Ons</h2>
        <Button type="primary" icon={<Plus size={15} />} onClick={openAddModal}>Add Service</Button>
      </div>

      {services.map(svc => (
        <div key={svc.id} className="service-card">
          <div className="service-card-header">
            <div>
              <div className="service-card-title">{svc.name}</div>
              <div className="service-card-meta">
                <span><Clock size={12} /> {svc.duration} min</span>
                <span><DollarSign size={12} /> ${svc.defaultPrice.toFixed(2)}</span>
              </div>
            </div>
            <div className="service-card-btns">
              <button className="icon-btn" onClick={() => openEditModal(svc)}><Edit2 size={15} /></button>
              <button className="icon-btn danger" onClick={() => removeService(svc.id)}><Trash2 size={15} /></button>
            </div>
          </div>

          <div className="addon-list">
            <div className="addon-list-header">
              <span className="addon-list-label">Add-Ons</span>
              <Button type="link" size="small" icon={<Plus size={13} />}
                onClick={() => { setAddingAddOnTo(addingAddOnTo === svc.id ? null : svc.id); setAddonName(''); setAddonPrice(''); }}>
                Add Add-On
              </Button>
            </div>

            {svc.addOns.length === 0 && addingAddOnTo !== svc.id && (
              <p style={{ fontSize: 13, color: '#9ca3af', fontStyle: 'italic', margin: 0 }}>No add-ons yet</p>
            )}

            {svc.addOns.map(ao => (
              <div key={ao.id} className="addon-item">
                <span className="addon-item-name">{ao.name}</span>
                <div className="addon-item-right">
                  <span className="addon-price">+${ao.price.toFixed(2)}</span>
                  <button className="icon-btn danger" style={{ width: 24, height: 24 }} onClick={() => removeAddon(svc.id, ao.id)}>
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            ))}

            {addingAddOnTo === svc.id && (
              <div className="addon-inline-form">
                <Input
                  placeholder="Add-on name" value={addonName} onChange={e => setAddonName(e.target.value)}
                  style={{ width: 180 }} size="small"
                />
                <Input
                  prefix="+$" type="number" placeholder="0.00" value={addonPrice}
                  onChange={e => setAddonPrice(e.target.value)} style={{ width: 90 }} size="small"
                />
                <Button size="small" type="primary" onClick={() => saveAddon(svc.id)}>Save</Button>
                <Button size="small" onClick={() => setAddingAddOnTo(null)}>Cancel</Button>
              </div>
            )}
          </div>
        </div>
      ))}

      <Modal
        title={editingSvc ? 'Edit Service' : 'Add Service'}
        open={!!(editingSvc || addingSvc)}
        onOk={handleModalOk}
        onCancel={() => { setEditingSvc(null); setAddingSvc(false); form.resetFields(); }}
        okText="Save"
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="name" label="Service Name" rules={[{ required: true }]}>
            <Input placeholder="e.g. Men's Haircut" />
          </Form.Item>
          <Form.Item name="defaultPrice" label="Default Price ($)" rules={[{ required: true }]}>
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="duration" label="Duration (minutes)" rules={[{ required: true }]}>
            <InputNumber min={5} step={5} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

// ─── Staff Management Tab ──────────────────────────────────────
type StaffSubTab = 'details' | 'notifications' | 'permissions' | 'services' | 'work-hours' | 'compensation' | 'time-clock';

const DEFAULT_PERMISSIONS: StaffPermissions = {
  can_change_appointment_status: true, can_book_own_appointments: true, can_view_others_calendar: false,
  can_book_others_appointments: false, can_create_recurring_blocks: false, can_create_others_time_blocks: false, can_change_waitlist: false,
  can_access_client_contact_details: true, can_view_client_last_names: true, can_adjust_client_balances: false,
  can_view_individual_sales_on_calendar: false, can_view_own_sales_list: true,
};
const DEFAULT_COMPENSATION: StaffCompensation = {
  service_commission_enabled: false, service_commission_type: 'basic', service_commission_basic_rate: 0, service_commission_scale_type: 'zero-based', service_commission_tiers: [],
  product_commission_enabled: false, product_commission_type: 'basic', product_commission_basic_rate: 0, product_commission_scale_type: 'zero-based', product_commission_tiers: [],
  hourly_enabled: false, hourly_rate: 0, hourly_greater_of: false,
  salary_enabled: false, monthly_salary: 0,
};
function defaultWorkHour(dayOfWeek: number): StaffWorkHour {
  return { day_of_week: dayOfWeek, is_off: dayOfWeek === 0, start_time: '9:00', end_time: '18:00', lunch_start: null, lunch_end: null };
}
function roleLabel(roleName: string) {
  const r = roleName.toLowerCase();
  if (r === 'admin') return 'Owner';
  if (r === 'barber') return 'Service Provider';
  if (r === 'front_desk') return 'Front Desk';
  return roleName;
}

// ─── Sliding-scale commission tier editor ──────────────────────
function TierEditor({ tiers, scaleType, onChange, onScaleTypeChange }: {
  tiers: CommissionTier[];
  scaleType: 'zero-based' | 'progressive';
  onChange: (tiers: CommissionTier[]) => void;
  onScaleTypeChange: (t: 'zero-based' | 'progressive') => void;
}) {
  function updateTier(i: number, patch: Partial<CommissionTier>) {
    onChange(tiers.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
  }
  function removeTier(i: number) {
    onChange(tiers.filter((_, idx) => idx !== i));
  }
  function addTier() {
    const lastBounded = [...tiers].reverse().find(t => t.up_to !== null);
    const nextFrom = (lastBounded?.up_to ?? 0) + 1;
    const newTier: CommissionTier = { up_to: nextFrom + 999, rate: 10 };
    if (tiers.length === 0) {
      onChange([newTier, { up_to: null, rate: 15 }]);
    } else {
      onChange([...tiers.slice(0, -1), newTier, tiers[tiers.length - 1]]);
    }
  }

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 100px 32px', gap: 8, fontSize: 12, color: '#9ca3af', marginBottom: 4 }}>
        <span>From</span><span>To</span><span>Rate</span><span />
      </div>
      {tiers.map((tier, i) => {
        const prevUpTo = i === 0 ? -0.01 : (tiers[i - 1].up_to ?? 0);
        const fromVal = i === 0 ? 0 : prevUpTo + 0.01;
        const isLast = i === tiers.length - 1;
        return (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 100px 32px', gap: 8, alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontSize: 13, color: '#374151' }}>${fromVal.toFixed(2)}</span>
            {isLast ? (
              <span style={{ fontSize: 13, color: '#374151' }}>and up</span>
            ) : (
              <InputNumber size="small" value={tier.up_to ?? 0} min={fromVal} prefix="$"
                onChange={v => updateTier(i, { up_to: v ?? 0 })} style={{ width: '100%' }} />
            )}
            <InputNumber size="small" value={tier.rate} min={0} max={100} step={0.5} formatter={v => `${v}%`}
              parser={v => parseFloat(v?.replace('%', '') ?? '0')}
              onChange={v => updateTier(i, { rate: v ?? 0 })} style={{ width: '100%' }} />
            <Button size="small" type="text" danger icon={<Trash2 size={13} />} disabled={tiers.length <= 1} onClick={() => removeTier(i)} />
          </div>
        );
      })}
      <Button size="small" type="link" icon={<Plus size={13} />} onClick={addTier} style={{ paddingLeft: 0 }}>Add tier</Button>

      <div style={{ marginTop: 12 }}>
        <Radio.Group value={scaleType} onChange={e => onScaleTypeChange(e.target.value)}>
          <Space direction="vertical">
            <Radio value="zero-based">
              <div style={{ fontSize: 13, fontWeight: 500 }}>Zero-based</div>
              <div style={{ fontSize: 12, color: '#9ca3af' }}>Rate of highest tier reached applies to total sales</div>
            </Radio>
            <Radio value="progressive">
              <div style={{ fontSize: 13, fontWeight: 500 }}>Progressive</div>
              <div style={{ fontSize: 12, color: '#9ca3af' }}>Percentages only apply to their respective tiers</div>
            </Radio>
          </Space>
        </Radio.Group>
      </div>
    </div>
  );
}

function StaffManagementTab({ services }: { services: Service[] }) {
  const [staffList, setStaffList] = useState<StaffMember[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [activeSubTab, setActiveSubTab] = useState<StaffSubTab>('permissions');
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [permissions, setPermissions] = useState<StaffPermissions>(DEFAULT_PERMISSIONS);
  const [compensation, setCompensation] = useState<StaffCompensation>(DEFAULT_COMPENSATION);
  const [staffNotifs, setStaffNotifs] = useState<StaffNotificationSettings>(DEFAULT_STAFF_NOTIFICATIONS);
  const [detailForm, setDetailForm] = useState({ name: '', phone: '', avatarUrl: '', disabled: false });
  const [savingDetails, setSavingDetails] = useState(false);
  const detailPhotoInputRef = useRef<HTMLInputElement>(null);
  const [workHours, setWorkHours] = useState<{ [day: string]: StaffWorkHour }>({});
  const [overrides, setOverrides] = useState<StaffOverride[]>([]);
  const [servicePricing, setServicePricing] = useState<{ [serviceId: string]: number }>({});

  const [addStaffVisible, setAddStaffVisible] = useState(false);
  const [addForm] = Form.useForm();
  const [tempPasswordInfo, setTempPasswordInfo] = useState<{ name: string; password: string } | null>(null);

  const [showAddOverride, setShowAddOverride] = useState(false);
  const [overrideDate, setOverrideDate] = useState('');
  const [overrideIsOff, setOverrideIsOff] = useState(false);
  const [overrideStart, setOverrideStart] = useState('9:00');
  const [overrideEnd, setOverrideEnd] = useState('18:00');
  const [staffClockEntries, setStaffClockEntries] = useState<ClockEntry[]>([]);

  function fetchStaffList() {
    axiosClient.get('/staff').then(res => setStaffList(res.data ?? [])).catch(() => {});
  }

  useEffect(() => { fetchStaffList(); }, []);

  useEffect(() => {
    if (staffList.length && !selectedId) setSelectedId(staffList[0].user_id);
  }, [staffList]);

  useEffect(() => {
    if (!selectedId) return;
    setLoadingDetail(true);
    Promise.all([
      axiosClient.get(`/staff/${selectedId}/permissions`),
      axiosClient.get(`/staff/${selectedId}/compensation`),
      axiosClient.get(`/staff/${selectedId}/work-hours`),
      axiosClient.get(`/staff/${selectedId}/overrides`),
      axiosClient.get(`/staff/${selectedId}/services`),
      axiosClient.get(`/staff/${selectedId}/notifications`),
    ]).then(([permRes, compRes, whRes, ovRes, svcRes, notifRes]) => {
      setPermissions({ ...DEFAULT_PERMISSIONS, ...permRes.data });
      setCompensation({ ...DEFAULT_COMPENSATION, ...compRes.data });
      setStaffNotifs({ ...DEFAULT_STAFF_NOTIFICATIONS, ...notifRes.data });
      const whMap: { [day: string]: StaffWorkHour } = {};
      (whRes.data ?? []).forEach((h: StaffWorkHour) => { whMap[INT_TO_DAY[h.day_of_week]] = h; });
      setWorkHours(whMap);
      setOverrides(ovRes.data ?? []);
      const svcMap: { [serviceId: string]: number } = {};
      (svcRes.data ?? []).forEach((s: StaffServicePrice) => { svcMap[s.service_id] = s.price; });
      setServicePricing(svcMap);
    }).catch(() => {}).finally(() => setLoadingDetail(false));
  }, [selectedId]);

  const selected = staffList.find(s => s.user_id === selectedId);

  function fetchStaffClockEntries() {
    if (!selectedId) return;
    axiosClient.get(`/staff/${selectedId}/clock-entries`).then(res => setStaffClockEntries(res.data ?? [])).catch(() => {});
  }

  useEffect(() => { fetchStaffClockEntries(); }, [selectedId]);

  async function approveClockEntry(entryId: string) {
    if (!selectedId) return;
    try {
      await axiosClient.post(`/staff/${selectedId}/clock-entries/${entryId}/approve`);
      fetchStaffClockEntries();
    } catch {
      message.error('Failed to approve clock entry.');
    }
  }

  useEffect(() => {
    if (!selected) return;
    setDetailForm({
      name: selected.name ?? '',
      phone: selected.phone ?? '',
      avatarUrl: selected.avatar_url ?? '',
      disabled: !!selected.disabled,
    });
  }, [selected?.user_id]);

  async function handleDetailPhotoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await resizeImageToDataUrl(file);
      setDetailForm(f => ({ ...f, avatarUrl: dataUrl }));
    } catch {
      message.error('Failed to process image.');
    }
  }

  async function saveStaffDetails() {
    if (!detailForm.name.trim()) {
      message.error('Name is required.');
      return;
    }
    setSavingDetails(true);
    try {
      await axiosClient.put(`/staff/${selectedId}`, {
        name: detailForm.name,
        phone: detailForm.phone,
        avatar_url: detailForm.avatarUrl,
        disabled: detailForm.disabled,
      });
      setStaffList(list => list.map(s => s.user_id === selectedId
        ? { ...s, name: detailForm.name, phone: detailForm.phone, avatar_url: detailForm.avatarUrl, disabled: detailForm.disabled }
        : s));
      message.success('Staff details saved.');
    } catch {
      message.error('Failed to save staff details.');
    } finally {
      setSavingDetails(false);
    }
  }

  async function savePermissions() {
    try {
      await axiosClient.put(`/staff/${selectedId}/permissions`, permissions);
      message.success('Permissions saved.');
    } catch {
      message.error('Failed to save permissions.');
    }
  }

  async function saveCompensation() {
    try {
      await axiosClient.put(`/staff/${selectedId}/compensation`, compensation);
      message.success('Compensation saved.');
    } catch {
      message.error('Failed to save compensation.');
    }
  }

  async function saveStaffNotifications() {
    try {
      await axiosClient.put(`/staff/${selectedId}/notifications`, staffNotifs);
      message.success('Notification settings saved.');
    } catch {
      message.error('Failed to save notification settings.');
    }
  }

  async function saveWorkHours() {
    const payload = DAYS.map(d => workHours[d] ?? defaultWorkHour(DAY_TO_INT[d]));
    try {
      await axiosClient.put(`/staff/${selectedId}/work-hours`, payload);
      message.success('Work hours saved.');
    } catch {
      message.error('Failed to save work hours.');
    }
  }

  async function saveServices() {
    const payload: StaffServicePrice[] = Object.entries(servicePricing).map(([service_id, price]) => ({ service_id, price }));
    try {
      await axiosClient.put(`/staff/${selectedId}/services`, payload);
      message.success('Services saved.');
    } catch {
      message.error('Failed to save services.');
    }
  }

  async function addOverride() {
    if (!overrideDate) return;
    try {
      await axiosClient.post(`/staff/${selectedId}/overrides`, {
        override_date: overrideDate, is_off: overrideIsOff, start_time: overrideStart, end_time: overrideEnd,
      });
      const res = await axiosClient.get(`/staff/${selectedId}/overrides`);
      setOverrides(res.data ?? []);
      setShowAddOverride(false);
      setOverrideDate(''); setOverrideIsOff(false); setOverrideStart('9:00'); setOverrideEnd('18:00');
    } catch {
      message.error('Failed to add override.');
    }
  }

  async function removeOverride(date: string) {
    try {
      await axiosClient.delete(`/staff/${selectedId}/overrides/${date}`);
      setOverrides(prev => prev.filter(o => o.override_date !== date));
    } catch {
      message.error('Failed to remove override.');
    }
  }

  async function handleAddStaff() {
    const vals = await addForm.validateFields();
    try {
      const resp = await axiosClient.post('/staff', { name: vals.name, email: vals.email, role_id: vals.role_id });
      setAddStaffVisible(false);
      addForm.resetFields();
      setTempPasswordInfo({ name: vals.name, password: resp.data.temp_password });
      fetchStaffList();
    } catch {
      message.error('Failed to add staff member.');
    }
  }

  const SUB_TABS: { key: StaffSubTab; label: string }[] = [
    { key: 'details', label: 'Details' },
    { key: 'notifications', label: 'Notifications' },
    { key: 'permissions', label: 'Permissions' },
    { key: 'services', label: 'Services' },
    { key: 'work-hours', label: 'Work Hours' },
    { key: 'compensation', label: 'Compensation' },
    ...(compensation.hourly_enabled ? [{ key: 'time-clock' as StaffSubTab, label: 'Time Clock' }] : []),
  ];

  return (
    <div>
      <div className="tab-top-bar">
        <h2 className="tab-section-title">Staff Management</h2>
        <Button type="primary" icon={<Plus size={15} />} onClick={() => setAddStaffVisible(true)}>Add Staff Member</Button>
      </div>

      <div className="staff-mgmt-wrap">
        {/* Sidebar */}
        <div className="staff-sidebar">
          <div className="staff-list-scroll">
            {staffList.map(s => (
              <button
                key={s.user_id}
                className={`staff-list-btn${selectedId === s.user_id ? ' active' : ''}`}
                onClick={() => setSelectedId(s.user_id)}
              >
                <div className="staff-avatar" style={s.avatar_url ? { padding: 0, overflow: 'hidden' } : undefined}>
                  {s.avatar_url
                    ? <img src={s.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                    : s.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </div>
                <div>
                  <div className="staff-list-name">{s.name}{s.disabled && <span style={{ color: '#ef4444', fontSize: 11, fontWeight: 500, marginLeft: 6 }}>Inactive</span>}</div>
                  <span className="staff-role-pill">{roleLabel(s.role_name)}</span>
                </div>
              </button>
            ))}
            {staffList.length === 0 && (
              <p style={{ padding: 16, fontSize: 13, color: '#9ca3af' }}>No staff yet. Add a staff member to get started.</p>
            )}
          </div>
        </div>

        {/* Detail panel */}
        {selected ? (
          <div className="staff-detail-panel">
            <Tabs
              activeKey={activeSubTab}
              onChange={k => setActiveSubTab(k as StaffSubTab)}
              style={{ padding: '0 24px' }}
              items={SUB_TABS.map(t => t.key === 'notifications'
                ? { key: t.key, label: comingSoonLabel(t.label), disabled: true }
                : { key: t.key, label: t.label })}
            />
            <div className="staff-detail-body">
              {loadingDetail && <p style={{ fontSize: 13, color: '#9ca3af' }}>Loading…</p>}

              {/* Details */}
              {!loadingDetail && activeSubTab === 'details' && (
                <div style={{ maxWidth: 480 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
                    <div style={{ position: 'relative', width: 64, height: 64 }}>
                      <div style={{
                        width: 64, height: 64, borderRadius: '50%', overflow: 'hidden',
                        background: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 22, fontWeight: 600, color: '#6b7280',
                      }}>
                        {detailForm.avatarUrl
                          ? <img src={detailForm.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          : (detailForm.name.slice(0, 1).toUpperCase() || '?')}
                      </div>
                      <button
                        type="button"
                        onClick={() => detailPhotoInputRef.current?.click()}
                        style={{
                          position: 'absolute', bottom: -2, right: -2, width: 24, height: 24, borderRadius: '50%',
                          background: '#2563eb', border: '2px solid #fff', color: '#fff', display: 'flex',
                          alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                        }}
                        title="Change photo"
                      >
                        <ImageIcon size={12} />
                      </button>
                      <input ref={detailPhotoInputRef} type="file" accept="image/*" hidden onChange={handleDetailPhotoChange} />
                    </div>
                    <Tag color="blue">{roleLabel(selected.role_name)}</Tag>
                  </div>

                  <Form layout="vertical">
                    <Form.Item label="Name">
                      <Input value={detailForm.name} onChange={e => setDetailForm(f => ({ ...f, name: e.target.value }))} />
                    </Form.Item>
                    <Form.Item label="Email"><Input value={selected.email ?? ''} readOnly disabled /></Form.Item>
                    <Form.Item label="Phone">
                      <Input value={detailForm.phone} placeholder="+1 (555) 000-0000"
                        onChange={e => setDetailForm(f => ({ ...f, phone: e.target.value }))} />
                    </Form.Item>
                  </Form>

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderTop: '1px solid #f0f0f0', marginTop: 4, marginBottom: 16 }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>Active</div>
                      <div style={{ fontSize: 12, color: '#9ca3af' }}>Inactive staff can't sign in</div>
                    </div>
                    <Switch checked={!detailForm.disabled}
                      onChange={v => setDetailForm(f => ({ ...f, disabled: !v }))} />
                  </div>

                  <Button type="primary" icon={<Save size={14} />} loading={savingDetails} onClick={saveStaffDetails}>Save Changes</Button>
                </div>
              )}

              {/* Notifications */}
              {!loadingDetail && activeSubTab === 'notifications' && (
                <div style={{ maxWidth: 480 }}>
                  <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
                    Choose which events trigger a notification for {selected.name.split(' ')[0]}.
                  </p>
                  {([
                    ['New Booking', 'email_new_booking', 'sms_new_booking'],
                    ['Cancellation', 'email_cancellation', 'sms_cancellation'],
                    ['Reschedule', 'email_reschedule', 'sms_reschedule'],
                    ['Reminder', 'email_reminder', 'sms_reminder'],
                  ] as [string, keyof StaffNotificationSettings, keyof StaffNotificationSettings][]).map(([label, emailKey, smsKey]) => (
                    <div key={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f0f0f0' }}>
                      <span style={{ fontSize: 14 }}>{label}</span>
                      <div style={{ display: 'flex', gap: 20 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#6b7280' }}>
                          Email
                          <Switch size="small" checked={!!staffNotifs[emailKey]}
                            onChange={v => setStaffNotifs({ ...staffNotifs, [emailKey]: v })} />
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#6b7280' }}>
                          SMS
                          <Switch size="small" checked={!!staffNotifs[smsKey]}
                            onChange={v => setStaffNotifs({ ...staffNotifs, [smsKey]: v })} />
                        </label>
                      </div>
                    </div>
                  ))}

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <span style={{ fontSize: 14 }}>Daily Summary</span>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#6b7280' }}>
                      Email
                      <Switch size="small" checked={staffNotifs.email_daily_summary}
                        onChange={v => setStaffNotifs({ ...staffNotifs, email_daily_summary: v })} />
                    </label>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <span style={{ fontSize: 14 }}>Weekly Report</span>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#6b7280' }}>
                      Email
                      <Switch size="small" checked={staffNotifs.email_weekly_report}
                        onChange={v => setStaffNotifs({ ...staffNotifs, email_weekly_report: v })} />
                    </label>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 16 }}>
                    <span style={{ fontSize: 13, color: '#6b7280' }}>Send reminder</span>
                    <InputNumber
                      value={staffNotifs.reminder_hours_before} min={1} max={72}
                      onChange={v => setStaffNotifs({ ...staffNotifs, reminder_hours_before: v ?? 2 })}
                      style={{ width: 70 }}
                    />
                    <span style={{ fontSize: 13, color: '#6b7280' }}>hours before appointment</span>
                  </div>

                  <div style={{ marginTop: 16 }}>
                    <Button type="primary" icon={<Save size={14} />} onClick={saveStaffNotifications}>Save Changes</Button>
                  </div>
                </div>
              )}

              {/* Permissions */}
              {!loadingDetail && activeSubTab === 'permissions' && (
                <div style={{ maxWidth: 680 }}>
                  <p className="perm-section-label">Calendar</p>
                  {([
                    ['can_change_appointment_status', 'Can change status of appointments', 'Confirm, check in/out, cancel, etc.'],
                    ['can_book_own_appointments', 'Can book and change own appointments', 'Book, cancel, and reschedule on their own calendar'],
                    ['can_view_others_calendar', "Can view other people's calendar", 'Viewing only — does not allow edits'],
                    ['can_book_others_appointments', "Can book and change other people's appointments", 'Book, cancel, and reschedule for other staff members'],
                    ['can_create_recurring_blocks', 'Can create and edit recurring time blocks', 'Add and remove time blocks on their own calendar'],
                    ['can_create_others_time_blocks', "Can create time blocks on other people's calendars", 'Manage time blocks for other staff'],
                    ['can_change_waitlist', 'Can change waitlist', 'Add and remove waitlist entries'],
                  ] as [keyof StaffPermissions, string, string][]).map(([key, title, desc]) => (
                    <div key={key} className="perm-item">
                      <Switch
                        checked={permissions[key]}
                        onChange={v => setPermissions({ ...permissions, [key]: v })}
                      />
                      <div className="perm-text"><h4>{title}</h4><p>{desc}</p></div>
                    </div>
                  ))}

                  <Divider />
                  <p className="perm-section-label">Clients</p>
                  {([
                    ['can_access_client_contact_details', "Can access clients' contact details", 'Phone number, email address, etc.'],
                    ['can_view_client_last_names', "Can view clients' last names", 'Disable to hide last names and remove access to the clients list'],
                    ['can_adjust_client_balances', 'Can manually adjust client account balances', "Increase or decrease a client's account balance"],
                  ] as [keyof StaffPermissions, string, string][]).map(([key, title, desc]) => (
                    <div key={key} className="perm-item">
                      <Switch
                        checked={permissions[key]}
                        onChange={v => setPermissions({ ...permissions, [key]: v })}
                      />
                      <div className="perm-text"><h4>{title}</h4><p>{desc}</p></div>
                    </div>
                  ))}

                  <Divider />
                  <p className="perm-section-label">Sales</p>
                  {([
                    ['can_view_individual_sales_on_calendar', 'Can view individual sales on calendar', 'View the sale attached to visible appointments'],
                    ['can_view_own_sales_list', 'Can view list of own sales', 'Grants access to their own sales list'],
                  ] as [keyof StaffPermissions, string, string][]).map(([key, title, desc]) => (
                    <div key={key} className="perm-item">
                      <Switch
                        checked={permissions[key]}
                        onChange={v => setPermissions({ ...permissions, [key]: v })}
                      />
                      <div className="perm-text"><h4>{title}</h4><p>{desc}</p></div>
                    </div>
                  ))}

                  <div style={{ marginTop: 16 }}>
                    <Button type="primary" icon={<Save size={14} />} onClick={savePermissions}>Save Changes</Button>
                  </div>
                </div>
              )}

              {/* Services */}
              {!loadingDetail && activeSubTab === 'services' && (
                <div style={{ maxWidth: 520 }}>
                  <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
                    Toggle which services {selected.name.split(' ')[0]} offers and set per-staff pricing.
                  </p>
                  {services.map(svc => {
                    const enabled = servicePricing[svc.id] !== undefined;
                    const price = servicePricing[svc.id] ?? svc.defaultPrice;
                    return (
                      <div key={svc.id} className={`staff-svc-item${enabled ? ' enabled' : ' disabled'}`}>
                        <Switch
                          checked={enabled}
                          onChange={v => {
                            const next = { ...servicePricing };
                            if (v) next[svc.id] = svc.defaultPrice; else delete next[svc.id];
                            setServicePricing(next);
                          }}
                          size="small"
                        />
                        <div className="staff-svc-info">
                          <div className="staff-svc-name">{svc.name}</div>
                          <div className="staff-svc-meta">{svc.duration} min · Default ${svc.defaultPrice.toFixed(2)}</div>
                        </div>
                        {enabled && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ fontSize: 13, color: '#6b7280' }}>$</span>
                            <InputNumber
                              value={price} min={0} step={0.01} size="small"
                              style={{ width: 80 }}
                              onChange={v => setServicePricing({ ...servicePricing, [svc.id]: v ?? 0 })}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div style={{ marginTop: 16 }}>
                    <Button type="primary" icon={<Save size={14} />} onClick={saveServices}>Save Changes</Button>
                  </div>
                </div>
              )}

              {/* Work Hours */}
              {!loadingDetail && activeSubTab === 'work-hours' && (
                <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
                  <div style={{ maxWidth: 480, flex: 1, minWidth: 320 }}>
                    <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
                      Set {selected.name.split(' ')[0]}'s weekly schedule.
                    </p>
                    {DAYS.map(day => {
                      const dh = workHours[day] ?? defaultWorkHour(DAY_TO_INT[day]);
                      const hasLunch = !!dh.lunch_start && !!dh.lunch_end;
                      return (
                        <div key={day} style={{ marginBottom: 8 }}>
                          <div className="wh-row">
                            <Switch
                              checked={!dh.is_off} size="small"
                              onChange={v => setWorkHours({ ...workHours, [day]: { ...dh, is_off: !v } })}
                            />
                            <span className="wh-day">{day.slice(0, 3)}</span>
                            {dh.is_off ? (
                              <span className="wh-closed-label">Day off</span>
                            ) : (
                              <>
                                <Select size="small" value={dh.start_time} options={TIME_OPTIONS} style={{ width: 110 }}
                                  onChange={v => setWorkHours({ ...workHours, [day]: { ...dh, start_time: v } })} />
                                <span className="hours-separator">–</span>
                                <Select size="small" value={dh.end_time} options={TIME_OPTIONS} style={{ width: 110 }}
                                  onChange={v => setWorkHours({ ...workHours, [day]: { ...dh, end_time: v } })} />
                              </>
                            )}
                          </div>
                          {!dh.is_off && (
                            <div style={{ marginLeft: 50, marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                              {hasLunch ? (
                                <>
                                  <span style={{ fontSize: 12, color: '#9ca3af' }}>Lunch</span>
                                  <Select size="small" value={dh.lunch_start ?? '12:00'} options={TIME_OPTIONS} style={{ width: 100 }}
                                    onChange={v => setWorkHours({ ...workHours, [day]: { ...dh, lunch_start: v } })} />
                                  <span className="hours-separator">–</span>
                                  <Select size="small" value={dh.lunch_end ?? '12:30'} options={TIME_OPTIONS} style={{ width: 100 }}
                                    onChange={v => setWorkHours({ ...workHours, [day]: { ...dh, lunch_end: v } })} />
                                  <Button size="small" type="text" icon={<X size={12} />}
                                    onClick={() => setWorkHours({ ...workHours, [day]: { ...dh, lunch_start: null, lunch_end: null } })} />
                                </>
                              ) : (
                                <Button size="small" type="link" icon={<Plus size={11} />} style={{ padding: 0, fontSize: 12 }}
                                  onClick={() => setWorkHours({ ...workHours, [day]: { ...dh, lunch_start: '12:00', lunch_end: '12:30' } })}>
                                  Add lunch
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    <div style={{ marginTop: 16 }}>
                      <Button type="primary" icon={<Save size={14} />} onClick={saveWorkHours}>Save Changes</Button>
                    </div>
                  </div>

                  <div style={{ maxWidth: 380, flex: 1, minWidth: 280 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                      <h3 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>Date Overrides</h3>
                      {!showAddOverride && (
                        <Button size="small" type="link" icon={<Plus size={13} />} onClick={() => setShowAddOverride(true)}>Add Override</Button>
                      )}
                    </div>
                    <p style={{ fontSize: 12, color: '#9ca3af', marginBottom: 12 }}>Override hours for a specific date (e.g. covering for someone).</p>

                    {overrides.length === 0 && !showAddOverride && (
                      <p style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>No date overrides yet.</p>
                    )}

                    {overrides.map(o => (
                      <div key={o.override_date} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', border: '1px solid #fde68a', background: '#fffbeb', borderRadius: 8, marginBottom: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 500 }}>{new Date(o.override_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        <span style={{ fontSize: 12, color: '#92400e' }}>
                          {o.is_off ? 'Day off' : `${o.start_time}–${o.end_time}`}
                        </span>
                        <Popconfirm title="Remove this override?" onConfirm={() => removeOverride(o.override_date)}>
                          <Button size="small" type="text" danger icon={<Trash2 size={12} />} style={{ marginLeft: 'auto' }} />
                        </Popconfirm>
                      </div>
                    ))}

                    {showAddOverride && (
                      <div style={{ border: '1px dashed #fbcfe8', borderRadius: 12, padding: 12, background: '#fdf2f8', marginTop: 8 }}>
                        <Space direction="vertical" style={{ width: '100%' }} size={8}>
                          <input type="date" value={overrideDate} onChange={e => setOverrideDate(e.target.value)}
                            style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, width: '100%' }} />
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Switch size="small" checked={!overrideIsOff} onChange={v => setOverrideIsOff(!v)} />
                            <span style={{ fontSize: 12 }}>{overrideIsOff ? 'Day off' : 'Working'}</span>
                          </div>
                          {!overrideIsOff && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <Select size="small" value={overrideStart} options={TIME_OPTIONS} style={{ width: 110 }} onChange={setOverrideStart} />
                              <span className="hours-separator">–</span>
                              <Select size="small" value={overrideEnd} options={TIME_OPTIONS} style={{ width: 110 }} onChange={setOverrideEnd} />
                            </div>
                          )}
                          <Space>
                            <Button size="small" type="primary" disabled={!overrideDate} onClick={addOverride}>Add Override</Button>
                            <Button size="small" onClick={() => setShowAddOverride(false)}>Cancel</Button>
                          </Space>
                        </Space>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Compensation */}
              {!loadingDetail && activeSubTab === 'compensation' && (
                <div style={{ maxWidth: 560 }}>
                  {/* Service Commission */}
                  <div className="comp-block">
                    <div className="comp-block-header">
                      <Switch checked={compensation.service_commission_enabled}
                        onChange={v => setCompensation({ ...compensation, service_commission_enabled: v })} />
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>Service Commission</div>
                        <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>Compensation as a % of service sales</div>
                      </div>
                    </div>
                    {compensation.service_commission_enabled && (
                      <div className="comp-block-body">
                        <Radio.Group
                          value={compensation.service_commission_type}
                          onChange={e => setCompensation({ ...compensation, service_commission_type: e.target.value })}
                        >
                          <Space direction="vertical">
                            <Radio value="basic">Basic — flat percentage</Radio>
                            <Radio value="sliding">Sliding scale — percentage depends on amount sold</Radio>
                          </Space>
                        </Radio.Group>

                        {compensation.service_commission_type === 'basic' ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
                            <span style={{ fontSize: 13, color: '#6b7280' }}>Rate</span>
                            <InputNumber
                              value={compensation.service_commission_basic_rate} min={0} max={100} step={0.5}
                              formatter={v => `${v}%`} parser={v => parseFloat(v?.replace('%', '') ?? '0')}
                              onChange={v => setCompensation({ ...compensation, service_commission_basic_rate: v ?? 0 })}
                              style={{ width: 100 }}
                            />
                          </div>
                        ) : (
                          <TierEditor
                            tiers={compensation.service_commission_tiers}
                            scaleType={compensation.service_commission_scale_type}
                            onChange={tiers => setCompensation({ ...compensation, service_commission_tiers: tiers })}
                            onScaleTypeChange={t => setCompensation({ ...compensation, service_commission_scale_type: t })}
                          />
                        )}
                      </div>
                    )}
                  </div>

                  {/* Product Commission */}
                  <div className="comp-block">
                    <div className="comp-block-header">
                      <Switch checked={compensation.product_commission_enabled}
                        onChange={v => setCompensation({ ...compensation, product_commission_enabled: v })} />
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>Product Commission</div>
                        <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>Compensation as a % of product sales</div>
                      </div>
                    </div>
                    {compensation.product_commission_enabled && (
                      <div className="comp-block-body">
                        <Radio.Group
                          value={compensation.product_commission_type}
                          onChange={e => setCompensation({ ...compensation, product_commission_type: e.target.value })}
                        >
                          <Space direction="vertical">
                            <Radio value="basic">Basic — flat percentage</Radio>
                            <Radio value="sliding">Sliding scale — percentage depends on amount sold</Radio>
                          </Space>
                        </Radio.Group>

                        {compensation.product_commission_type === 'basic' ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
                            <span style={{ fontSize: 13, color: '#6b7280' }}>Rate</span>
                            <InputNumber
                              value={compensation.product_commission_basic_rate} min={0} max={100} step={0.5}
                              formatter={v => `${v}%`} parser={v => parseFloat(v?.replace('%', '') ?? '0')}
                              onChange={v => setCompensation({ ...compensation, product_commission_basic_rate: v ?? 0 })}
                              style={{ width: 100 }}
                            />
                          </div>
                        ) : (
                          <TierEditor
                            tiers={compensation.product_commission_tiers}
                            scaleType={compensation.product_commission_scale_type}
                            onChange={tiers => setCompensation({ ...compensation, product_commission_tiers: tiers })}
                            onScaleTypeChange={t => setCompensation({ ...compensation, product_commission_scale_type: t })}
                          />
                        )}
                      </div>
                    )}
                  </div>

                  {/* Hourly */}
                  <div className="comp-block">
                    <div className="comp-block-header">
                      <Switch checked={compensation.hourly_enabled}
                        onChange={v => setCompensation({ ...compensation, hourly_enabled: v })} />
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>Hourly</div>
                        <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>Flat rate per hour worked</div>
                      </div>
                    </div>
                    {compensation.hourly_enabled && (
                      <div className="comp-block-body">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 13, color: '#6b7280' }}>Rate</span>
                          <InputNumber
                            value={compensation.hourly_rate} min={0} step={0.5}
                            formatter={v => `$${v}`} parser={v => parseFloat(v?.replace('$', '') ?? '0')}
                            onChange={v => setCompensation({ ...compensation, hourly_rate: v ?? 0 })}
                            style={{ width: 100 }}
                          />
                          <span style={{ fontSize: 13, color: '#6b7280' }}>/hr</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 14 }}>
                          <Switch checked={compensation.hourly_greater_of}
                            onChange={v => setCompensation({ ...compensation, hourly_greater_of: v })} />
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>Enable greater-of calculation</div>
                            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
                              Staff member is paid the higher of their hourly rate or commission earned for the period
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Salary */}
                  <div className="comp-block">
                    <div className="comp-block-header">
                      <Switch checked={compensation.salary_enabled}
                        onChange={v => setCompensation({ ...compensation, salary_enabled: v })} />
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>Salary</div>
                        <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>Fixed monthly pay, independent of hours or sales</div>
                      </div>
                    </div>
                    {compensation.salary_enabled && (
                      <div className="comp-block-body">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 13, color: '#6b7280' }}>Amount</span>
                          <InputNumber
                            value={compensation.monthly_salary} min={0} step={50}
                            formatter={v => `$${v}`} parser={v => parseFloat(v?.replace('$', '') ?? '0')}
                            onChange={v => setCompensation({ ...compensation, monthly_salary: v ?? 0 })}
                            style={{ width: 120 }}
                          />
                          <span style={{ fontSize: 13, color: '#6b7280' }}>/mo</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div style={{ marginTop: 8 }}>
                    <Button type="primary" icon={<Save size={14} />} onClick={saveCompensation}>Save Changes</Button>
                  </div>
                </div>
              )}

              {/* Time Clock — admin review/approve, hourly staff only */}
              {!loadingDetail && activeSubTab === 'time-clock' && (
                <div style={{ maxWidth: 640 }}>
                  <p style={{ fontSize: 13, color: '#6b7280', marginTop: -4, marginBottom: 16 }}>
                    Clock entries this staff member logs themselves from their own Profile Settings. Approve once the hours look right — approved entries are what feed Payroll.
                  </p>
                  {staffClockEntries.length === 0 ? (
                    <p style={{ fontSize: 13, color: '#9ca3af', fontStyle: 'italic' }}>No clock entries yet.</p>
                  ) : (
                    <Space direction="vertical" size={6} style={{ width: '100%' }}>
                      {staffClockEntries.map(e => (
                        <div key={e.entry_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', border: '1px solid #f0f0f0', borderRadius: 8, fontSize: 13 }}>
                          <span>{new Date(e.clock_in).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                          <span style={{ color: '#6b7280' }}>
                            {new Date(e.clock_in).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                            {' – '}
                            {e.clock_out ? new Date(e.clock_out).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : 'now'}
                          </span>
                          <span style={{ fontWeight: 600 }}>{netHours(e)}{e.clock_out ? ' hrs' : ''}</span>
                          <Tag color={e.status === 'approved' ? 'success' : e.status === 'pending' ? 'warning' : 'processing'}>{e.status}</Tag>
                          {e.status === 'pending' && (
                            <Button size="small" type="primary" onClick={() => approveClockEntry(e.entry_id)}>Approve</Button>
                          )}
                        </div>
                      ))}
                    </Space>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 14 }}>
            Select a staff member
          </div>
        )}
      </div>

      {/* Add Staff Modal */}
      <Modal
        title="Add Staff Member"
        open={addStaffVisible}
        onOk={handleAddStaff}
        onCancel={() => { setAddStaffVisible(false); addForm.resetFields(); }}
        okText="Add Staff Member"
      >
        <Form form={addForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="name" label="Full Name" rules={[{ required: true }]}>
            <Input placeholder="Jane Smith" />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
            <Input placeholder="jane@yoursalon.com" />
          </Form.Item>
          <Form.Item name="role_id" label="Role" rules={[{ required: true }]}>
            <Select options={[
              { label: 'Service Provider', value: 2 },
              { label: 'Front Desk', value: 3 },
              { label: 'Owner', value: 1 },
            ]} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Temp Password Modal */}
      <Modal
        title="Staff Member Added"
        open={!!tempPasswordInfo}
        onOk={() => setTempPasswordInfo(null)}
        onCancel={() => setTempPasswordInfo(null)}
        okText="Done"
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        <p>Share this temporary password with <strong>{tempPasswordInfo?.name}</strong> — they should change it after logging in.</p>
        <Tag style={{ fontSize: 16, padding: '6px 12px', fontFamily: 'monospace' }}>{tempPasswordInfo?.password}</Tag>
      </Modal>
    </div>
  );
}

// ─── Business Hours Tab ────────────────────────────────────────
function BusinessHoursTab() {
  const [hours, setHours] = useState(INITIAL_HOURS);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [taxRatePercent, setTaxRatePercent] = useState(8);
  const [taxSaving, setTaxSaving] = useState(false);
  const [taxSaved, setTaxSaved] = useState(false);
  const [businessName, setBusinessName] = useState('');
  const [businessAddress, setBusinessAddress] = useState('');
  const [infoSaving, setInfoSaving] = useState(false);
  const [infoSaved, setInfoSaved] = useState(false);

  const [holidays, setHolidays] = useState<BusinessHoliday[]>([]);
  const [showAddHoliday, setShowAddHoliday] = useState(false);
  const [holidayDate, setHolidayDate] = useState('');
  const [holidayLabel, setHolidayLabel] = useState('');
  const [holidayClosed, setHolidayClosed] = useState(true);
  const [holidayOpen, setHolidayOpen] = useState('9:00');
  const [holidayClose, setHolidayClose] = useState('17:00');
  const [savingHoliday, setSavingHoliday] = useState(false);

  const stripSec = (t: string) => t.slice(0, 5).replace(/^0(\d:)/, '$1');

  function fetchHolidays() {
    axiosClient.get('/settings/holiday-hours').then(res => setHolidays(res.data ?? [])).catch(() => {});
  }

  useEffect(() => {
    axiosClient.get('/settings/hours').then(res => {
      const data: { day_of_week: number; is_open: boolean; open_time: string; close_time: string }[] = res.data ?? [];
      if (data.length === 0) return;
      const next = { ...INITIAL_HOURS };
      for (const row of data) {
        const name = INT_TO_DAY[row.day_of_week];
        if (name) next[name] = { open: stripSec(row.open_time), close: stripSec(row.close_time), closed: !row.is_open };
      }
      setHours(next);
    }).catch(() => {});
    axiosClient.get('/business').then(res => {
      if (typeof res.data?.tax_rate === 'number') setTaxRatePercent(res.data.tax_rate * 100);
      if (typeof res.data?.name === 'string') setBusinessName(res.data.name);
      if (typeof res.data?.address === 'string') setBusinessAddress(res.data.address);
    }).catch(() => {});
    fetchHolidays();
  }, []);

  async function addHoliday() {
    if (!holidayDate) return;
    setSavingHoliday(true);
    try {
      await axiosClient.post('/settings/holiday-hours', {
        holiday_date: holidayDate,
        label: holidayLabel || null,
        is_closed: holidayClosed,
        open_time: holidayClosed ? null : holidayOpen,
        close_time: holidayClosed ? null : holidayClose,
      });
      fetchHolidays();
      setShowAddHoliday(false);
      setHolidayDate(''); setHolidayLabel(''); setHolidayClosed(true);
      setHolidayOpen('9:00'); setHolidayClose('17:00');
      message.success('Holiday hours saved.');
    } catch {
      message.error('Failed to save holiday hours.');
    } finally {
      setSavingHoliday(false);
    }
  }

  async function removeHoliday(date: string) {
    try {
      await axiosClient.delete(`/settings/holiday-hours/${date}`);
      setHolidays(prev => prev.filter(h => h.holiday_date !== date));
    } catch {
      message.error('Failed to remove holiday hours.');
    }
  }

  async function saveBusinessInfo() {
    setInfoSaving(true);
    try {
      await axiosClient.put('/business', { name: businessName, address: businessAddress });
      setInfoSaved(true);
      message.success('Business info saved.');
      setTimeout(() => setInfoSaved(false), 2500);
    } catch {
      message.error('Failed to save business info.');
    } finally {
      setInfoSaving(false);
    }
  }

  async function saveTaxRate() {
    setTaxSaving(true);
    try {
      await axiosClient.put('/business/tax-rate', { tax_rate: taxRatePercent / 100 });
      setTaxSaved(true);
      message.success('Tax rate saved.');
      setTimeout(() => setTaxSaved(false), 2500);
    } catch {
      message.error('Failed to save tax rate.');
    } finally {
      setTaxSaving(false);
    }
  }

  function patch(day: string, change: Partial<DayHours>) {
    setHours(prev => ({ ...prev, [day]: { ...prev[day], ...change } }));
  }

  async function save() {
    setSaving(true);
    try {
      const payload = DAYS.map(day => ({
        day_of_week: DAY_TO_INT[day],
        is_open: !hours[day].closed,
        open_time: hours[day].open,
        close_time: hours[day].close,
      }));
      await axiosClient.put('/settings/hours', payload);
      setSaved(true);
      message.success('Business hours saved.');
      setTimeout(() => setSaved(false), 2500);
    } catch {
      message.error('Failed to save business hours.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div className="tab-top-bar">
        <h2 className="tab-section-title">Business Info</h2>
        <Button type="primary" icon={<Save size={15} />} onClick={saveBusinessInfo} loading={infoSaving}>
          {infoSaved ? 'Saved!' : 'Save Changes'}
        </Button>
      </div>
      <div className="hours-card" style={{ marginBottom: 24, padding: 16 }}>
        <div style={{ maxWidth: 480 }}>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4, color: '#374151' }}>Business Name</label>
            <Input value={businessName} onChange={e => setBusinessName(e.target.value)} placeholder="Your business name" />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4, color: '#374151' }}>Address</label>
            <Input value={businessAddress} onChange={e => setBusinessAddress(e.target.value)} placeholder="Street, City, State ZIP" />
          </div>
        </div>
      </div>

      <div className="tab-top-bar">
        <h2 className="tab-section-title">Sales Tax Rate</h2>
        <Button type="primary" icon={<Save size={15} />} onClick={saveTaxRate} loading={taxSaving}>
          {taxSaved ? 'Saved!' : 'Save Changes'}
        </Button>
      </div>
      <div className="hours-card" style={{ marginBottom: 24 }}>
        <div className="hours-row">
          <span className="hours-day-label">Tax Rate</span>
          <InputNumber
            value={taxRatePercent}
            onChange={v => setTaxRatePercent(typeof v === 'number' ? v : 0)}
            min={0}
            max={100}
            step={0.1}
            precision={1}
            formatter={v => `${v}%`}
            parser={v => Number((v ?? '').replace('%', ''))}
            style={{ width: 120 }}
          />
          <span style={{ fontSize: 13, color: '#6b7280' }}>
            Applied to every POS checkout as sales tax
          </span>
        </div>
      </div>

      <div className="tab-top-bar">
        <h2 className="tab-section-title">Business Hours</h2>
        <Button type="primary" icon={<Save size={15} />} onClick={save} loading={saving}>
          {saved ? 'Saved!' : 'Save Changes'}
        </Button>
      </div>
      <div className="hours-card">
        {DAYS.map(day => {
          const h = hours[day];
          return (
            <div key={day} className="hours-row">
              <span className="hours-day-label">{day}</span>
              <Switch
                checked={!h.closed} size="small"
                onChange={v => patch(day, { closed: !v })}
              />
              <span style={{ fontSize: 13, color: '#6b7280', minWidth: 44 }}>{h.closed ? 'Closed' : 'Open'}</span>
              {!h.closed && (
                <>
                  <Select size="small" value={h.open} options={TIME_OPTIONS} style={{ width: 120 }}
                    onChange={v => patch(day, { open: v })} />
                  <span className="hours-separator">to</span>
                  <Select size="small" value={h.close} options={TIME_OPTIONS} style={{ width: 120 }}
                    onChange={v => patch(day, { close: v })} />
                </>
              )}
            </div>
          );
        })}
      </div>

      <div className="tab-top-bar" style={{ marginTop: 24 }}>
        <h2 className="tab-section-title">Holiday Hours</h2>
        {!showAddHoliday && (
          <Button icon={<Plus size={14} />} onClick={() => setShowAddHoliday(true)}>Add Holiday</Button>
        )}
      </div>
      <div className="hours-card" style={{ padding: 16 }}>
        <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 0, marginBottom: 12 }}>
          Override your weekly hours for a specific date — closed for a holiday, or special hours. This affects real online-booking availability for every staff member that date.
        </p>

        {holidays.length === 0 && !showAddHoliday && (
          <p style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>No holiday overrides yet.</p>
        )}

        {holidays.map(h => (
          <div key={h.holiday_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', border: '1px solid #fde68a', background: '#fffbeb', borderRadius: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 500 }}>{new Date(h.holiday_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
            {h.label && <span style={{ fontSize: 12, color: '#374151' }}>{h.label}</span>}
            <span style={{ fontSize: 12, color: '#92400e' }}>
              {h.is_closed ? 'Closed' : `${stripSec(h.open_time ?? '')}–${stripSec(h.close_time ?? '')}`}
            </span>
            <Popconfirm title="Remove this holiday override?" onConfirm={() => removeHoliday(h.holiday_date)}>
              <Button size="small" type="text" danger icon={<Trash2 size={12} />} style={{ marginLeft: 'auto' }} />
            </Popconfirm>
          </div>
        ))}

        {showAddHoliday && (
          <div style={{ border: '1px dashed #fbcfe8', borderRadius: 12, padding: 12, background: '#fdf2f8', marginTop: 8, maxWidth: 420 }}>
            <Space direction="vertical" style={{ width: '100%' }} size={8}>
              <input type="date" value={holidayDate} onChange={e => setHolidayDate(e.target.value)}
                style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, width: '100%' }} />
              <Input size="small" placeholder="Label (optional, e.g. Christmas Day)" value={holidayLabel} onChange={e => setHolidayLabel(e.target.value)} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Switch size="small" checked={!holidayClosed} onChange={v => setHolidayClosed(!v)} />
                <span style={{ fontSize: 12 }}>{holidayClosed ? 'Closed' : 'Special hours'}</span>
              </div>
              {!holidayClosed && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Select size="small" value={holidayOpen} options={TIME_OPTIONS} style={{ width: 120 }} onChange={setHolidayOpen} />
                  <span className="hours-separator">to</span>
                  <Select size="small" value={holidayClose} options={TIME_OPTIONS} style={{ width: 120 }} onChange={setHolidayClose} />
                </div>
              )}
              <Space>
                <Button size="small" type="primary" loading={savingHoliday} disabled={!holidayDate} onClick={addHoliday}>Save Holiday</Button>
                <Button size="small" onClick={() => setShowAddHoliday(false)}>Cancel</Button>
              </Space>
            </Space>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Cash Drawer Tab ───────────────────────────────────────────
function CashDrawerTab() {
  const [businessId, setBusinessId] = useState<string | null>(null);

  useEffect(() => {
    axiosClient.get('/business').then(res => {
      if (res.data?.business_id) setBusinessId(res.data.business_id);
    }).catch(() => {});
  }, []);

  return (
    <div>
      <div className="tab-top-bar">
        <h2 className="tab-section-title">Cash Drawer</h2>
      </div>
      <div style={{ maxWidth: 540 }}>
        {businessId && <CashDrawerSettingsPage businessId={businessId} />}
      </div>
    </div>
  );
}

// ─── Scheduling Page Tab ───────────────────────────────────────
function SchedulingPageTab({ barbers }: { barbers: Barber[] }) {
  const [salonName, setSalonName] = useState('The Hair Plane');
  const [salonTagline, setSalonTagline] = useState('Premium haircare in a relaxed, modern setting');
  const [salonDescription, setSalonDescription] = useState('Welcome to The Hair Plane — where every visit is a first-class experience. Our skilled stylists specialise in precision cuts, colour, and treatments tailored to you.');
  const [salonLogo, setSalonLogo] = useState<string | null>(null);
  const [salonSlug, setSalonSlug] = useState('');
  const [bookingEnabled, setBookingEnabled] = useState(false);
  const [steps, setSteps] = useState<BookingStep[]>(INITIAL_STEPS);
  const [staffOrder, setStaffOrder] = useState<string[]>([]);
  const [savedStaffOrder, setSavedStaffOrder] = useState<string[] | null>(null);
  const [waitlistEnabled, setWaitlistEnabled] = useState(false);
  const [waitlistNoticeMinutes, setWaitlistNoticeMinutes] = useState(60);
  const [advanceDays, setAdvanceDays] = useState(30);
  const [bufferMinutes, setBufferMinutes] = useState(0);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    axiosClient.get('/settings/scheduling').then(res => {
      const c = res.data;
      if (!c) { setSavedStaffOrder([]); return; }
      if (c.page_title)       setSalonName(c.page_title);
      if (c.page_description) setSalonDescription(c.page_description);
      if (typeof c.booking_enabled === 'boolean') setBookingEnabled(c.booking_enabled);
      if (c.tagline)   setSalonTagline(c.tagline);
      if (c.logo_url)  setSalonLogo(c.logo_url);
      if (c.page_slug) setSalonSlug(c.page_slug);
      const hidden: string[] = c.hidden_steps ?? [];
      setSteps(INITIAL_STEPS.map(s => ({ ...s, visible: s.required ? true : !hidden.includes(s.id) })));
      setSavedStaffOrder(Array.isArray(c.staff_order) ? c.staff_order : []);
      if (typeof c.waitlist_enabled === 'boolean') setWaitlistEnabled(c.waitlist_enabled);
      if (typeof c.waitlist_notice_minutes === 'number') setWaitlistNoticeMinutes(c.waitlist_notice_minutes);
      if (typeof c.advance_days === 'number') setAdvanceDays(c.advance_days);
      if (typeof c.buffer_minutes === 'number') setBufferMinutes(c.buffer_minutes);
    }).catch(() => setSavedStaffOrder([]));
  }, []);

  useEffect(() => {
    if (savedStaffOrder === null || barbers.length === 0) return;
    const validSaved = savedStaffOrder.filter(id => barbers.some(b => b.user_id === id));
    const missing = barbers.map(b => b.user_id).filter(id => !validSaved.includes(id));
    setStaffOrder([...validSaved, ...missing]);
  }, [barbers, savedStaffOrder]);

  async function handleLogoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setSalonLogo(await resizeImageToDataUrl(file, 200));
    } catch {
      message.error('Failed to process image.');
    }
    e.target.value = '';
  }

  function handleSlugChange(v: string) {
    setSalonSlug(v.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/-+/g, '-'));
  }

  function moveStaff(idx: number, dir: 'up' | 'down') {
    const next = [...staffOrder];
    const target = dir === 'up' ? idx - 1 : idx + 1;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setStaffOrder(next);
  }

  async function save() {
    setSaving(true);
    try {
      await axiosClient.put('/settings/scheduling', {
        booking_enabled:  bookingEnabled,
        page_title:       salonName,
        page_description: salonDescription,
        page_slug:        salonSlug,
        tagline:          salonTagline,
        logo_url:         salonLogo ?? '',
        hidden_steps:     steps.filter(s => !s.visible).map(s => s.id),
        staff_order:      staffOrder,
        waitlist_enabled: waitlistEnabled,
        waitlist_notice_minutes: waitlistNoticeMinutes,
        advance_days:     advanceDays,
        buffer_minutes:   bufferMinutes,
      });
      setSaved(true);
      message.success('Scheduling page settings saved.');
      setTimeout(() => setSaved(false), 2500);
    } catch {
      message.error('Failed to save scheduling page settings.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div className="tab-top-bar">
        <h2 className="tab-section-title">Scheduling Page</h2>
        <Button type="primary" icon={<Save size={15} />} onClick={save} loading={saving}>
          {saved ? 'Saved!' : 'Save Changes'}
        </Button>
      </div>

      {/* Online Booking Toggle */}
      <div className="sched-card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div className="sched-card-title" style={{ marginBottom: 4 }}><Globe size={17} /> Online Booking</div>
          <div className="sched-card-desc" style={{ marginBottom: 0 }}>Allow clients to book appointments through your public scheduling page.</div>
        </div>
        <Switch checked={bookingEnabled} onChange={setBookingEnabled} checkedChildren="On" unCheckedChildren="Off" />
      </div>

      {/* Advance booking window & buffer time */}
      <div className="sched-card">
        <div className="sched-card-title" style={{ marginBottom: 4 }}><Clock size={17} /> Booking Window</div>
        <div className="sched-card-desc">Control how far ahead clients can book, and the gap left between back-to-back appointments.</div>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 8 }}>
          <div style={{ minWidth: 220 }}>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>How far in advance clients can book</div>
            <InputNumber
              value={advanceDays}
              onChange={v => setAdvanceDays(v ?? 30)}
              min={1}
              max={365}
              addonAfter="days"
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ minWidth: 220 }}>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Buffer between appointments</div>
            <InputNumber
              value={bufferMinutes}
              onChange={v => setBufferMinutes(v ?? 0)}
              min={0}
              max={120}
              step={5}
              addonAfter="min"
              style={{ width: '100%' }}
            />
          </div>
        </div>
      </div>


      {/* Booking Page URL */}
      <div className="sched-card">
        <div className="sched-card-title"><Globe size={17} /> Booking Page URL</div>
        <div className="sched-card-desc">The link customers use to reach your online booking page.</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, maxWidth: 480 }}>
          <span style={{ fontSize: 13, color: '#9ca3af', whiteSpace: 'nowrap' }}>/book/</span>
          <Input value={salonSlug} onChange={e => handleSlugChange(e.target.value)} placeholder="your-salon-name" />
        </div>
        {!salonSlug && (
          <p style={{ fontSize: 12, color: '#dc2626', marginTop: 6 }}>Set a URL to make your booking page reachable.</p>
        )}
      </div>

      {/* Salon Description */}
      <div className="sched-card">
        <div className="sched-card-title"><Globe size={17} /> Salon Description</div>
        <div className="sched-card-desc">Customise what customers see on your booking page.</div>

        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', marginBottom: 20 }}>
          <div className="logo-upload-box" onClick={() => logoInputRef.current?.click()}>
            {salonLogo
              ? <img src={salonLogo} alt="Salon logo" />
              : <ImageIcon size={28} color="#d1d5db" />}
          </div>
          <input ref={logoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoUpload} />
          <div>
            <Button icon={<Upload size={13} />} onClick={() => logoInputRef.current?.click()}>
              {salonLogo ? 'Replace Logo' : 'Upload Logo'}
            </Button>
            {salonLogo && (
              <Button danger style={{ marginLeft: 8 }} onClick={() => setSalonLogo(null)}>Remove</Button>
            )}
            <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 6 }}>PNG, JPG, SVG · Recommended 400×400 px</p>
          </div>
        </div>

        <Form layout="vertical" style={{ maxWidth: 560 }}>
          <Form.Item label="Salon Name">
            <Input value={salonName} onChange={e => setSalonName(e.target.value)} />
          </Form.Item>
          <Form.Item label="Tagline">
            <Input value={salonTagline} onChange={e => setSalonTagline(e.target.value)}
              placeholder="A short phrase shown beneath the salon name" />
          </Form.Item>
          <Form.Item label="About the Salon">
            <Input.TextArea value={salonDescription} onChange={e => setSalonDescription(e.target.value)}
              rows={4} showCount maxLength={500} />
          </Form.Item>
        </Form>

        {/* Live preview */}
        <div style={{ border: '1px dashed #bfdbfe', borderRadius: 12, background: '#eff6ff', padding: 16, maxWidth: 560 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: '#3b82f6', textTransform: 'uppercase', letterSpacing: 1, margin: '0 0 10px' }}>Booking page preview</p>
          <div style={{ background: 'white', borderRadius: 8, padding: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 6 }}>
              {salonLogo && <img src={salonLogo} alt="" style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'contain' }} />}
              <div>
                <p style={{ fontWeight: 700, fontSize: 16, margin: 0, color: '#111827' }}>{salonName || 'Your Salon Name'}</p>
                <p style={{ fontSize: 13, color: '#2563eb', margin: 0 }}>{salonTagline || 'Your tagline'}</p>
              </div>
            </div>
            <p style={{ fontSize: 13, color: '#6b7280', margin: 0, lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {salonDescription || 'Your salon description will appear here.'}
            </p>
          </div>
        </div>
      </div>

      {/* Booking Steps */}
      <div className="sched-card">
        <div className="sched-card-title"><Users size={17} /> Booking Steps</div>
        <div className="sched-card-desc">Control which steps appear in the customer booking flow. Required steps cannot be hidden.</div>
        {steps.map((step, i) => (
          <div key={step.id} className="booking-step-item">
            <div className="booking-step-left">
              <div className="booking-step-num">{i + 1}</div>
              <div>
                <div className="booking-step-name">
                  {step.label}
                  {step.required && <span className="required-tag">Required</span>}
                </div>
                <div className="booking-step-sub">{step.description}</div>
              </div>
            </div>
            <Button
              size="small"
              disabled={step.required}
              icon={step.visible ? <Eye size={13} /> : <EyeOff size={13} />}
              onClick={() => setSteps(steps.map(s => s.id === step.id ? { ...s, visible: !s.visible } : s))}
              style={{ color: step.visible ? '#059669' : '#9ca3af' }}
            >
              {step.visible ? 'Visible' : 'Hidden'}
            </Button>
          </div>
        ))}
      </div>

      {/* Staff Display Order */}
      {staffOrder.length > 0 && (
        <div className="sched-card">
          <div className="sched-card-title"><Users size={17} /> Staff Display Order</div>
          <div className="sched-card-desc">Set the order staff members appear in the customer's stylist selection.</div>
          {staffOrder.map((id, idx) => {
            const barber = barbers.find(b => b.user_id === id);
            if (!barber) return null;
            return (
              <div key={id} className="staff-order-item">
                <span style={{ width: 20, fontSize: 13, fontWeight: 700, color: '#d1d5db' }}>{idx + 1}</span>
                <div className="staff-avatar" style={{ width: 32, height: 32, fontSize: 11 }}>
                  {barber.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </div>
                <span style={{ fontSize: 14, fontWeight: 500, color: '#111827', flex: 1 }}>{barber.name}</span>
                <div className="staff-order-controls">
                  <button className="reorder-btn" disabled={idx === 0} onClick={() => moveStaff(idx, 'up')}>
                    <ChevronUp size={14} />
                  </button>
                  <button className="reorder-btn" disabled={idx === staffOrder.length - 1} onClick={() => moveStaff(idx, 'down')}>
                    <ChevronDown size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Reminder timing chip editor (email/SMS "send X before" offsets) ───
function TimingEditor({ timings, onChange }: { timings: ReminderTiming[]; onChange: (t: ReminderTiming[]) => void }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Send reminders</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {timings.map((t, i) => (
          <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8, padding: '2px 6px' }}>
            <InputNumber
              size="small" min={1} value={t.value}
              onChange={v => onChange(timings.map((x, xi) => xi === i ? { ...x, value: typeof v === 'number' ? v : 1 } : x))}
              style={{ width: 56 }}
            />
            <Select
              size="small" value={t.unit} variant="borderless"
              onChange={v => onChange(timings.map((x, xi) => xi === i ? { ...x, unit: v } : x))}
              options={[
                { value: 'minutes', label: 'min before' },
                { value: 'hours', label: 'hrs before' },
                { value: 'days', label: 'days before' },
              ]}
              style={{ width: 90 }}
            />
            <button
              onClick={() => onChange(timings.filter((_, xi) => xi !== i))}
              style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#059669', display: 'flex' }}
            >
              <X size={13} />
            </button>
          </div>
        ))}
        <Button
          size="small" type="dashed" icon={<Plus size={12} />}
          onClick={() => onChange([...timings, { id: `rt-${Date.now()}`, value: 2, unit: 'hours' }])}
        >
          Add Reminder
        </Button>
      </div>
    </div>
  );
}

function RecipientToggle({ value, onChange }: { value: 'clients' | 'clients_admins'; onChange: (v: 'clients' | 'clients_admins') => void }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Send to</div>
      <Space>
        <Button size="small" type={value === 'clients' ? 'primary' : 'default'} onClick={() => onChange('clients')}>Clients only</Button>
        <Button size="small" type={value === 'clients_admins' ? 'primary' : 'default'} onClick={() => onChange('clients_admins')}>Clients & admins</Button>
      </Space>
    </div>
  );
}

// ─── Waitlist Tab ────────────────────────────────────────────
// Shares hp.scheduling_config with the Scheduling Page tab via the same
// GET/PUT /settings/scheduling endpoint, so this fetches and round-trips the
// FULL config (not just the waitlist fields) to avoid clobbering the other
// tab's settings on save — same pattern SchedulingPageTab itself uses.
const WAITLIST_NOTICE_OPTIONS = [
  { value: 0, label: 'No minimum' },
  { value: 30, label: '30 minutes' },
  { value: 60, label: '1 hour' },
  { value: 120, label: '2 hours' },
  { value: 180, label: '3 hours' },
  { value: 240, label: '4 hours' },
  { value: 360, label: '6 hours' },
  { value: 720, label: '12 hours' },
];

function WaitlistTab() {
  const [config, setConfig] = useState<Record<string, unknown> | null>(null);
  const [waitlistEnabled, setWaitlistEnabled] = useState(false);
  const [waitlistNoticeMinutes, setWaitlistNoticeMinutes] = useState(60);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    axiosClient.get('/settings/scheduling').then(res => {
      const c = res.data ?? {};
      setConfig(c);
      if (typeof c.waitlist_enabled === 'boolean') setWaitlistEnabled(c.waitlist_enabled);
      if (typeof c.waitlist_notice_minutes === 'number') setWaitlistNoticeMinutes(c.waitlist_notice_minutes);
    }).catch(() => setConfig({})).finally(() => setLoading(false));
  }, []);

  async function save() {
    setSaving(true);
    try {
      await axiosClient.put('/settings/scheduling', {
        ...config,
        waitlist_enabled: waitlistEnabled,
        waitlist_notice_minutes: waitlistNoticeMinutes,
      });
      setSaved(true);
      message.success('Waitlist settings saved.');
      setTimeout(() => setSaved(false), 2500);
    } catch {
      message.error('Failed to save waitlist settings.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p style={{ fontSize: 13, color: '#9ca3af' }}>Loading…</p>;

  return (
    <div style={{ maxWidth: 560 }}>
      <div className="tab-top-bar">
        <h2 className="tab-section-title">Waitlist</h2>
        <Button type="primary" icon={<Save size={15} />} onClick={save} loading={saving}>
          {saved ? 'Saved!' : 'Save Changes'}
        </Button>
      </div>

      <p style={{ fontSize: 13, color: '#6b7280', marginTop: -8, marginBottom: 16 }}>
        Maximize your availability and fill last-minute cancellations by offering a waitlist to clients.
        When a slot opens up, waitlisted clients can be booked instantly — keeping your calendar full.
      </p>

      <div className="sched-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div className="sched-card-title" style={{ marginBottom: 4 }}><Users size={17} /> Enable Waitlist</div>
            <div className="sched-card-desc" style={{ marginBottom: 0 }}>Allow clients to join a waitlist from your booking page when your schedule is fully booked.</div>
          </div>
          <Switch checked={waitlistEnabled} onChange={setWaitlistEnabled} checkedChildren="On" unCheckedChildren="Off" />
        </div>
        {!waitlistEnabled && (
          <p style={{ fontSize: 12, color: '#b45309', marginTop: 12, marginBottom: 0 }}>
            Waitlist is currently off. Clients won't see a waitlist option when your schedule is full.
          </p>
        )}
      </div>

      <div className="sched-card" style={{ opacity: waitlistEnabled ? 1 : 0.4, pointerEvents: waitlistEnabled ? 'auto' : 'none' }}>
        <div className="sched-card-title" style={{ marginBottom: 4 }}>Minimum Notice</div>
        <div className="sched-card-desc">How far in advance a waitlisted client must book a freed-up slot. Prevents last-second bookings you can't prepare for.</div>
        <Select
          value={waitlistNoticeMinutes}
          onChange={setWaitlistNoticeMinutes}
          style={{ width: '100%', maxWidth: 320 }}
          options={WAITLIST_NOTICE_OPTIONS}
        />
      </div>
    </div>
  );
}

// ─── Booking Protection Tab (no-show fee policy + double-booking scan) ────────
interface BookingConflict {
  booking_a_id: string;
  booking_b_id: string;
  staff_name: string;
  client_a_name: string;
  client_b_name: string;
  service_a: string;
  service_b: string;
  start_a: string;
  end_a: string;
  start_b: string;
  end_b: string;
}

interface ClientBookingConflict {
  conflict_id: string;
  client_name: string;
  service_a: string;
  service_b: string;
  staff_a_name: string;
  staff_b_name: string;
  start_a: string;
  start_b: string;
  conflict_type: 'same-staff-same-day' | 'multi-staff-same-day' | 'multi-day';
  status: 'pending' | 'resolved' | 'auto-cancelled';
  alert_sent: boolean;
}

const CB_TYPE_LABEL: Record<ClientBookingConflict['conflict_type'], string> = {
  'same-staff-same-day': 'Same staff · same day',
  'multi-staff-same-day': 'Different staff · same day',
  'multi-day': 'Nearby days',
};

function BookingProtectionTab() {
  const [penaltyType, setPenaltyType] = useState<'percent' | 'fixed'>('percent');
  const [penaltyAmount, setPenaltyAmount] = useState(50);
  const [windowHours, setWindowHours] = useState(24);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [conflicts, setConflicts] = useState<BookingConflict[]>([]);
  const [scanning, setScanning] = useState(false);

  const [cbSettings, setCbSettings] = useState({
    enabled: true, same_staff_same_day: true, any_staff_same_day: true, multi_day: true,
    grace_period_hours: 24, notify_email: true, notify_sms: true, auto_cancel: true,
  });
  const [cbSaving, setCbSaving] = useState(false);
  const [cbSaved, setCbSaved] = useState(false);
  const [clientConflicts, setClientConflicts] = useState<ClientBookingConflict[]>([]);
  const [cbScanning, setCbScanning] = useState(false);

  useEffect(() => {
    axiosClient.get('/settings/no-show-policy').then(res => {
      const p = res.data ?? {};
      if (p.penalty_type) setPenaltyType(p.penalty_type);
      if (typeof p.penalty_amount === 'number') setPenaltyAmount(p.penalty_amount);
      if (typeof p.window_hours === 'number') setWindowHours(p.window_hours);
    }).catch(() => {}).finally(() => setLoading(false));
    scanConflicts();
    axiosClient.get('/settings/client-booking-protection').then(res => setCbSettings(res.data)).catch(() => {});
    scanClientConflicts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function scanClientConflicts(showResultMessage = false) {
    setCbScanning(true);
    axiosClient.get('/settings/client-booking-conflicts')
      .then(res => {
        const found = res.data ?? [];
        setClientConflicts(found);
        if (showResultMessage) {
          if (found.length === 0) message.success('Scan complete — no client conflicts found.');
          else message.warning(`Scan complete — ${found.length} client conflict${found.length === 1 ? '' : 's'} found.`);
        }
      })
      .catch(() => {
        if (showResultMessage) message.error('Scan failed.');
      })
      .finally(() => setCbScanning(false));
  }

  async function saveClientBookingProtection() {
    setCbSaving(true);
    try {
      await axiosClient.put('/settings/client-booking-protection', cbSettings);
      setCbSaved(true);
      message.success('Settings saved.');
      setTimeout(() => setCbSaved(false), 2500);
      scanClientConflicts();
    } catch {
      message.error('Failed to save settings.');
    } finally {
      setCbSaving(false);
    }
  }

  async function resolveClientConflict(id: string) {
    try {
      await axiosClient.post(`/settings/client-booking-conflicts/${id}/resolve`);
      setClientConflicts(prev => prev.map(c => c.conflict_id === id ? { ...c, status: 'resolved' } : c));
    } catch {
      message.error('Failed to resolve conflict.');
    }
  }

  function scanConflicts(showResultMessage = false) {
    setScanning(true);
    axiosClient.get('/settings/double-booking-conflicts')
      .then(res => {
        const found = res.data ?? [];
        setConflicts(found);
        if (showResultMessage) {
          if (found.length === 0) message.success('Scan complete — no conflicts found.');
          else message.warning(`Scan complete — ${found.length} conflict${found.length === 1 ? '' : 's'} found.`);
        }
      })
      .catch(() => {
        setConflicts([]);
        if (showResultMessage) message.error('Scan failed.');
      })
      .finally(() => setScanning(false));
  }

  async function save() {
    setSaving(true);
    try {
      await axiosClient.put('/settings/no-show-policy', {
        penalty_type: penaltyType,
        penalty_amount: penaltyAmount,
        window_hours: windowHours,
      });
      setSaved(true);
      message.success('No-show policy saved.');
      setTimeout(() => setSaved(false), 2500);
    } catch {
      message.error('Failed to save no-show policy.');
    } finally {
      setSaving(false);
    }
  }

  const fmtTime = (iso: string) => new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

  if (loading) return <p style={{ fontSize: 13, color: '#9ca3af' }}>Loading…</p>;

  return (
    <div style={{ maxWidth: 640 }}>
      <div className="tab-top-bar">
        <h2 className="tab-section-title">Booking Protection</h2>
        <Button type="primary" icon={<Save size={15} />} onClick={save} loading={saving}>
          {saved ? 'Saved!' : 'Save Changes'}
        </Button>
      </div>
      <p style={{ fontSize: 13, color: '#6b7280', marginTop: -8, marginBottom: 16 }}>
        Configure the no-show fee suggested when charging a client who didn't show up, and scan for
        real scheduling conflicts (the same staff member double-booked for overlapping times).
      </p>

      <div className="sched-card">
        <div className="sched-card-title" style={{ marginBottom: 4 }}><DollarSign size={17} /> No-Show Fee</div>
        <div className="sched-card-desc">
          This is a starting suggestion staff can adjust — it's applied manually via "Charge No-Show Fee" on an appointment, not collected automatically at booking time.
        </div>
        <Space wrap style={{ marginTop: 12 }}>
          <InputNumber
            min={0}
            precision={penaltyType === 'percent' ? 0 : 2}
            max={penaltyType === 'percent' ? 100 : undefined}
            prefix={penaltyType === 'fixed' ? '$' : undefined}
            suffix={penaltyType === 'percent' ? '%' : undefined}
            value={penaltyAmount}
            onChange={(v) => setPenaltyAmount(v ?? 0)}
            style={{ width: 140 }}
          />
          <Radio.Group value={penaltyType} onChange={(e) => setPenaltyType(e.target.value)}>
            <Radio.Button value="percent">% of price</Radio.Button>
            <Radio.Button value="fixed">$ fixed</Radio.Button>
          </Radio.Group>
        </Space>
        <div className="sched-card-title" style={{ marginTop: 20, marginBottom: 4 }}>Applies within</div>
        <div className="sched-card-desc">How close to the appointment time counts as a chargeable no-show.</div>
        <InputNumber min={1} max={168} value={windowHours} onChange={(v) => setWindowHours(v ?? 24)} style={{ width: 140, marginTop: 8 }} addonAfter="hours" />
      </div>

      <div className="sched-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div className="sched-card-title" style={{ marginBottom: 4 }}>Double-Booking Scan</div>
          <Button size="small" icon={<RefreshCw size={13} />} loading={scanning} onClick={() => scanConflicts(true)}>Rescan</Button>
        </div>
        <div className="sched-card-desc">
          Scans for the same staff member booked into two overlapping appointments — real conflicts in your data, not a simulation.
        </div>
        {conflicts.length === 0 ? (
          <p style={{ fontSize: 13, color: '#16a34a', marginTop: 12 }}>✓ No conflicts found.</p>
        ) : (
          <Space direction="vertical" size={10} style={{ width: '100%', marginTop: 12 }}>
            {conflicts.map((c) => (
              <div key={`${c.booking_a_id}-${c.booking_b_id}`} style={{ border: '1px solid #fecaca', background: '#fef2f2', borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#991b1b', marginBottom: 4 }}>{c.staff_name} — double-booked</div>
                <div style={{ fontSize: 12, color: '#7f1d1d' }}>{c.client_a_name} · {c.service_a} · {fmtTime(c.start_a)}–{fmtTime(c.end_a)}</div>
                <div style={{ fontSize: 12, color: '#7f1d1d' }}>{c.client_b_name} · {c.service_b} · {fmtTime(c.start_b)}–{fmtTime(c.end_b)}</div>
                <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 6, marginBottom: 0 }}>
                  Resolve from the Calendar page — open either appointment and Reschedule or Cancel it.
                </p>
              </div>
            ))}
          </Space>
        )}
      </div>

      <div className="sched-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div className="sched-card-title" style={{ marginBottom: 4 }}>Client Double-Booking Protection</div>
          <Space>
            <Button size="small" icon={<RefreshCw size={13} />} loading={cbScanning} onClick={() => scanClientConflicts(true)}>Rescan</Button>
            <Button size="small" type="primary" loading={cbSaving} onClick={saveClientBookingProtection}>{cbSaved ? 'Saved!' : 'Save'}</Button>
          </Space>
        </div>
        <div className="sched-card-desc">
          Detects the same client holding multiple upcoming bookings that look like a mistake — not a staff scheduling bug, a client booking pattern. Real bookings, real detection; SMS alerts are logged, not actually sent, until a texting provider is connected.
        </div>

        <Space direction="vertical" size={10} style={{ width: '100%', marginTop: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 13 }}>Enabled</span>
            <Switch checked={cbSettings.enabled} onChange={v => setCbSettings(s => ({ ...s, enabled: v }))} />
          </div>
          {([
            ['same_staff_same_day', 'Same staff, same day'],
            ['any_staff_same_day', 'Different staff, same day'],
            ['multi_day', 'Within a few nearby days'],
          ] as const).map(([key, label]) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingLeft: 12 }}>
              <span style={{ fontSize: 13, color: '#6b7280' }}>{label}</span>
              <Switch size="small" checked={cbSettings[key]} onChange={v => setCbSettings(s => ({ ...s, [key]: v }))} />
            </div>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13 }}>Grace period before auto-cancel</span>
            <InputNumber min={1} max={168} value={cbSettings.grace_period_hours} onChange={v => setCbSettings(s => ({ ...s, grace_period_hours: v ?? 24 }))} style={{ width: 100 }} addonAfter="hrs" />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 13 }}>Auto-cancel the later booking if unresolved</span>
            <Switch checked={cbSettings.auto_cancel} onChange={v => setCbSettings(s => ({ ...s, auto_cancel: v }))} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 13 }}>Notify client by email</span>
            <Switch checked={cbSettings.notify_email} onChange={v => setCbSettings(s => ({ ...s, notify_email: v }))} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 13 }}>Notify client by SMS</span>
            <Switch checked={cbSettings.notify_sms} onChange={v => setCbSettings(s => ({ ...s, notify_sms: v }))} />
          </div>
        </Space>

        {clientConflicts.length === 0 ? (
          <p style={{ fontSize: 13, color: '#16a34a', marginTop: 16 }}>✓ No client booking conflicts found.</p>
        ) : (
          <Space direction="vertical" size={10} style={{ width: '100%', marginTop: 16 }}>
            {clientConflicts.map((c) => (
              <div key={c.conflict_id} style={{ border: '1px solid #fde68a', background: '#fffbeb', borderRadius: 10, padding: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#92400e' }}>{c.client_name} — {CB_TYPE_LABEL[c.conflict_type]}</span>
                  <Tag color={c.status === 'resolved' ? 'success' : c.status === 'auto-cancelled' ? 'default' : 'warning'}>{c.status}</Tag>
                </div>
                <div style={{ fontSize: 12, color: '#78350f' }}>{c.service_a} with {c.staff_a_name} · {fmtTime(c.start_a)}</div>
                <div style={{ fontSize: 12, color: '#78350f' }}>{c.service_b} with {c.staff_b_name} · {fmtTime(c.start_b)}</div>
                {c.status === 'pending' && (
                  <Button size="small" style={{ marginTop: 8 }} onClick={() => resolveClientConflict(c.conflict_id)}>Mark Resolved</Button>
                )}
              </div>
            ))}
          </Space>
        )}
      </div>
    </div>
  );
}

// ─── Client Notifications Tab ──────────────────────────────────
type NotifKey = 'bookingConfirmation' | 'reminders' | 'cancellation' | 'rescheduling';

const NOTIF_CARDS: { key: NotifKey; label: string; desc: string; icon: React.ReactNode }[] = [
  { key: 'bookingConfirmation', label: 'Booking Confirmation', desc: 'Sent immediately when a client books.', icon: <CheckCircle size={17} color="#059669" /> },
  { key: 'reminders',          label: 'Appointment Reminders', desc: 'Sent automatically before the appointment.', icon: <Clock size={17} color="#2563eb" /> },
  { key: 'cancellation',       label: 'Cancellation Notice',   desc: 'Sent when an appointment is canceled.', icon: <X size={17} color="#dc2626" /> },
  { key: 'rescheduling',       label: 'Reschedule Notice',     desc: 'Sent when an appointment is rescheduled.', icon: <RefreshCw size={17} color="#d97706" /> },
];

function ClientNotificationsTab() {
  const [notifs, setNotifs] = useState(INITIAL_NOTIFS);
  const [expanded, setExpanded] = useState<NotifKey | null>('bookingConfirmation');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    axiosClient.get('/settings/notifications').then(res => {
      const p = res.data;
      if (!p) return;
      setNotifs(prev => ({
        bookingConfirmation: {
          ...prev.bookingConfirmation,
          emailEnabled: p.email_confirmations ?? prev.bookingConfirmation.emailEnabled,
          smsEnabled: p.sms_confirmations ?? prev.bookingConfirmation.smsEnabled,
          emailSubject: p.confirmation_email_subject || prev.bookingConfirmation.emailSubject,
          emailBody: p.confirmation_email_body || prev.bookingConfirmation.emailBody,
          smsBody: p.confirmation_sms_body || prev.bookingConfirmation.smsBody,
        },
        reminders: {
          ...prev.reminders,
          emailEnabled: p.email_reminders ?? prev.reminders.emailEnabled,
          smsEnabled: p.sms_reminders ?? prev.reminders.smsEnabled,
          emailSubject: p.reminder_email_subject || prev.reminders.emailSubject,
          emailBody: p.reminder_email_body || prev.reminders.emailBody,
          smsBody: p.reminder_sms_body || prev.reminders.smsBody,
          emailTimings: Array.isArray(p.reminder_email_timings) && p.reminder_email_timings.length > 0
            ? p.reminder_email_timings.map((t: any, i: number) => ({ id: `rt-e${i}`, value: t.value, unit: t.unit }))
            : prev.reminders.emailTimings,
          smsTimings: Array.isArray(p.reminder_sms_timings) && p.reminder_sms_timings.length > 0
            ? p.reminder_sms_timings.map((t: any, i: number) => ({ id: `rt-s${i}`, value: t.value, unit: t.unit }))
            : prev.reminders.smsTimings,
          emailRecipients: p.reminder_email_recipients || prev.reminders.emailRecipients,
          smsRecipients: p.reminder_sms_recipients || prev.reminders.smsRecipients,
        },
        cancellation: {
          ...prev.cancellation,
          emailEnabled: p.email_cancellations ?? prev.cancellation.emailEnabled,
          emailSubject: p.cancellation_email_subject || prev.cancellation.emailSubject,
          emailBody: p.cancellation_email_body || prev.cancellation.emailBody,
          smsBody: p.cancellation_sms_body || prev.cancellation.smsBody,
        },
        rescheduling: {
          ...prev.rescheduling,
          emailSubject: p.reschedule_email_subject || prev.rescheduling.emailSubject,
          emailBody: p.reschedule_email_body || prev.rescheduling.emailBody,
          smsBody: p.reschedule_sms_body || prev.rescheduling.smsBody,
        },
      }));
    }).catch(() => {});
  }, []);

  function patch(key: NotifKey, change: Partial<NotifSection>) {
    setNotifs(prev => ({ ...prev, [key]: { ...prev[key], ...change } }));
  }

  function appendVar(key: NotifKey, field: 'emailBody' | 'smsBody' | 'emailSubject', v: string) {
    const cur = notifs[key][field];
    patch(key, { [field]: cur ? `${cur} ${v}` : v });
  }

  async function save() {
    try {
      await axiosClient.put('/settings/notifications', {
        email_confirmations: notifs.bookingConfirmation.emailEnabled,
        sms_confirmations:   notifs.bookingConfirmation.smsEnabled,
        email_reminders:     notifs.reminders.emailEnabled,
        sms_reminders:       notifs.reminders.smsEnabled,
        email_cancellations: notifs.cancellation.emailEnabled,
        marketing_emails:    false,
        confirmation_email_subject: notifs.bookingConfirmation.emailSubject,
        confirmation_email_body:    notifs.bookingConfirmation.emailBody,
        confirmation_sms_body:      notifs.bookingConfirmation.smsBody,
        reminder_email_subject:     notifs.reminders.emailSubject,
        reminder_email_body:        notifs.reminders.emailBody,
        reminder_sms_body:          notifs.reminders.smsBody,
        cancellation_email_subject: notifs.cancellation.emailSubject,
        cancellation_email_body:    notifs.cancellation.emailBody,
        cancellation_sms_body:      notifs.cancellation.smsBody,
        reschedule_email_subject:   notifs.rescheduling.emailSubject,
        reschedule_email_body:      notifs.rescheduling.emailBody,
        reschedule_sms_body:        notifs.rescheduling.smsBody,
        reminder_email_timings: (notifs.reminders.emailTimings ?? []).map(t => ({ value: t.value, unit: t.unit })),
        reminder_sms_timings:   (notifs.reminders.smsTimings ?? []).map(t => ({ value: t.value, unit: t.unit })),
        reminder_email_recipients: notifs.reminders.emailRecipients,
        reminder_sms_recipients:   notifs.reminders.smsRecipients,
      });
      setSaved(true);
      message.success('Notification templates saved.');
      setTimeout(() => setSaved(false), 2500);
    } catch {
      message.error('Failed to save notification templates.');
    }
  }

  function updateReminderTimings(channel: 'emailTimings' | 'smsTimings', timings: ReminderTiming[]) {
    patch('reminders', { [channel]: timings } as Partial<NotifSection>);
  }

  function resetTemplate(key: NotifKey, channel: 'email' | 'sms') {
    const base = (INITIAL_NOTIFS as Record<NotifKey, NotifSection>)[key];
    if (channel === 'email') {
      patch(key, { emailSubject: base.emailSubject, emailBody: base.emailBody });
    } else {
      patch(key, { smsBody: base.smsBody });
    }
  }

  return (
    <div>
      <div className="tab-top-bar">
        <h2 className="tab-section-title">Client Notifications</h2>
        <Button type="primary" icon={<Save size={15} />} onClick={save}>
          {saved ? 'Saved!' : 'Save Changes'}
        </Button>
      </div>

      {NOTIF_CARDS.map(card => {
        const n = notifs[card.key];
        const isOpen = expanded === card.key;
        return (
          <div key={card.key} className="notif-card">
            <div className="notif-card-header" onClick={() => setExpanded(isOpen ? null : card.key)}>
              <div className="notif-header-left">
                {card.icon}
                <div>
                  <div className="notif-header-title">{card.label}</div>
                  <div className="notif-header-desc">{card.desc}</div>
                </div>
              </div>
              {isOpen ? <ChevronUp size={18} color="#9ca3af" /> : <ChevronDown size={18} color="#9ca3af" />}
            </div>

            {isOpen && (
              <div className="notif-card-body">
                {/* Email */}
                <div style={{ marginBottom: 24 }}>
                  <div className="notif-channel-header">
                    <Mail size={14} /> Email
                    <Switch
                      checked={n.emailEnabled} size="small"
                      onChange={v => patch(card.key, { emailEnabled: v })}
                      style={{ marginLeft: 'auto' }}
                    />
                  </div>
                  {n.emailEnabled && (
                    <Space direction="vertical" style={{ width: '100%' }}>
                      {card.key === 'reminders' && (
                        <>
                          <TimingEditor timings={n.emailTimings ?? []} onChange={t => updateReminderTimings('emailTimings', t)} />
                          <RecipientToggle value={n.emailRecipients ?? 'clients'} onChange={v => patch('reminders', { emailRecipients: v })} />
                        </>
                      )}
                      <div>
                        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Subject</div>
                        <div className="template-vars">
                          {TEMPLATE_VARS.map(v => (
                            <span key={v} className="tvar-chip" onClick={() => appendVar(card.key, 'emailSubject', v)}>{v}</span>
                          ))}
                        </div>
                        <Input
                          value={n.emailSubject}
                          onChange={e => patch(card.key, { emailSubject: e.target.value })}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Body</div>
                        <div className="template-vars">
                          {TEMPLATE_VARS.map(v => (
                            <span key={v} className="tvar-chip" onClick={() => appendVar(card.key, 'emailBody', v)}>{v}</span>
                          ))}
                        </div>
                        <Input.TextArea
                          value={n.emailBody} rows={5}
                          onChange={e => patch(card.key, { emailBody: e.target.value })}
                        />
                      </div>
                      <Button type="link" size="small" icon={<RefreshCw size={12} />} onClick={() => resetTemplate(card.key, 'email')} style={{ paddingLeft: 0 }}>
                        Reset to default template
                      </Button>
                    </Space>
                  )}
                </div>

                {/* SMS */}
                <div>
                  <div className="notif-channel-header">
                    <MessageSquare size={14} /> SMS
                    <Switch
                      checked={n.smsEnabled} size="small"
                      onChange={v => patch(card.key, { smsEnabled: v })}
                      style={{ marginLeft: 'auto' }}
                    />
                  </div>
                  {n.smsEnabled && (
                    <div>
                      {card.key === 'reminders' && (
                        <>
                          <TimingEditor timings={n.smsTimings ?? []} onChange={t => updateReminderTimings('smsTimings', t)} />
                          <RecipientToggle value={n.smsRecipients ?? 'clients'} onChange={v => patch('reminders', { smsRecipients: v })} />
                        </>
                      )}
                      <div className="template-vars">
                        {TEMPLATE_VARS.map(v => (
                          <span key={v} className="tvar-chip" onClick={() => appendVar(card.key, 'smsBody', v)}>{v}</span>
                        ))}
                      </div>
                      <Input.TextArea
                        value={n.smsBody} rows={3}
                        onChange={e => patch(card.key, { smsBody: e.target.value })}
                      />
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                        {n.smsBody.length} characters · ~{Math.ceil(n.smsBody.length / 160)} SMS segment{Math.ceil(n.smsBody.length / 160) !== 1 ? 's' : ''}
                      </div>
                      <Button type="link" size="small" icon={<RefreshCw size={12} />} onClick={() => resetTemplate(card.key, 'sms')} style={{ paddingLeft: 0, marginTop: 4 }}>
                        Reset to default template
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Employee Profile View ─────────────────────────────────────
const ROLE_BADGE_LABEL: Record<string, string> = {
  admin: 'Owner',
  barber: 'Service Provider',
  front_desk: 'Front Desk',
};

function passwordStrength(pw: string): { score: number; label: string; color: string } {
  if (!pw) return { score: 0, label: '', color: '#e5e7eb' };
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
  if (/[0-9]/.test(pw) && /[^A-Za-z0-9]/.test(pw)) score++;
  const levels = [
    { label: 'Weak', color: '#ef4444' },
    { label: 'Fair', color: '#f59e0b' },
    { label: 'Good', color: '#eab308' },
    { label: 'Strong', color: '#22c55e' },
  ];
  const idx = Math.min(score, levels.length - 1);
  return { score: idx + 1, label: levels[idx].label, color: levels[idx].color };
}

function resizeImageToDataUrl(file: File, size = 160): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject(new Error('canvas not supported'));
        const scale = Math.max(size / img.width, size / img.height);
        const w = img.width * scale, h = img.height * scale;
        ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      };
      img.onerror = reject;
      img.src = reader.result as string;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

interface ClockEntry {
  entry_id: string;
  clock_in: string;
  clock_out: string | null;
  break_minutes: number;
  status: 'active' | 'pending' | 'approved';
}

function netHours(e: ClockEntry): string {
  if (!e.clock_out) return '—';
  const mins = (new Date(e.clock_out).getTime() - new Date(e.clock_in).getTime()) / 60000 - e.break_minutes;
  return (Math.max(mins, 0) / 60).toFixed(2);
}

function EmployeeProfileView() {
  const auth = useAppSelector(s => s.auth);
  const dispatch = useAppDispatch();
  const [profile, setProfile] = useState({ name: '', email: auth.identifier ?? '', phone: '' });
  const [clockStatus, setClockStatus] = useState<ClockEntry | null>(null);
  const [clockEntries, setClockEntries] = useState<ClockEntry[]>([]);
  const [clockLoading, setClockLoading] = useState(false);
  const [breakMinutes, setBreakMinutes] = useState(0);
  const [avatarUrl, setAvatarUrl] = useState('');
  const [bio, setBio] = useState('');
  const [specialties, setSpecialties] = useState<string[]>([]);
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [notifPrefs, setNotifPrefs] = useState({
    email_notifications: true, sms_notifications: false, appointment_reminders: true, new_booking_alerts: true,
  });
  const [notifSaving, setNotifSaving] = useState(false);
  const [notifSaved, setNotifSaved] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);

  useEffect(() => {
    axiosClient.get('/me/notifications').then(res => setNotifPrefs(res.data)).catch(() => {});
    fetchClockData();
  }, []);

  function fetchClockData() {
    axiosClient.get('/time-clock/status').then(res => setClockStatus(res.data)).catch(() => {});
    axiosClient.get('/time-clock/my-entries').then(res => setClockEntries(res.data ?? [])).catch(() => {});
  }

  async function clockIn() {
    setClockLoading(true);
    try {
      await axiosClient.post('/time-clock/clock-in');
      message.success('Clocked in.');
      fetchClockData();
    } catch {
      message.error('Failed to clock in.');
    } finally {
      setClockLoading(false);
    }
  }

  async function clockOut() {
    setClockLoading(true);
    try {
      await axiosClient.post('/time-clock/clock-out', { break_minutes: breakMinutes });
      message.success('Clocked out.');
      setBreakMinutes(0);
      fetchClockData();
    } catch {
      message.error('Failed to clock out.');
    } finally {
      setClockLoading(false);
    }
  }

  async function saveNotificationPrefs() {
    setNotifSaving(true);
    try {
      await axiosClient.put('/me/notifications', notifPrefs);
      setNotifSaved(true);
      setTimeout(() => setNotifSaved(false), 2500);
    } catch {
      message.error('Failed to save notification preferences.');
    } finally {
      setNotifSaving(false);
    }
  }

  const roleLabel = ROLE_BADGE_LABEL[auth.role ?? ''] ?? auth.role ?? '';
  const strength = passwordStrength(newPassword);

  useEffect(() => {
    axiosClient.get('/me').then(res => {
      const u = res.data;
      setProfile({ name: u.name ?? '', email: u.email ?? auth.identifier ?? '', phone: u.phone ?? '' });
      setAvatarUrl(u.avatar_url ?? '');
      setBio(u.bio ?? '');
      setSpecialties(u.specialties ? String(u.specialties).split(',').filter(Boolean) : []);
      profileForm.setFieldsValue({ name: u.name, phone: u.phone, address: u.address ?? '' });
    }).catch(() => {});
  }, []);

  async function handlePhotoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await resizeImageToDataUrl(file);
      setAvatarUrl(dataUrl);
    } catch {
      message.error('Failed to process image.');
    }
  }

  async function saveProfile() {
    const vals = await profileForm.validateFields();
    setProfileSaving(true);
    try {
      await axiosClient.put('/me', {
        name: vals.name,
        phone: vals.phone ?? null,
        address: vals.address ?? '',
        bio,
        avatar_url: avatarUrl,
        specialties: specialties.join(','),
      });
      dispatch(setAuthAvatarUrl(avatarUrl || null));
      setProfileSaved(true);
      message.success('Profile updated.');
      setTimeout(() => setProfileSaved(false), 2500);
    } catch {
      message.error('Failed to update profile.');
    } finally {
      setProfileSaving(false);
    }
  }

  async function changePassword() {
    const vals = await passwordForm.validateFields();
    if (vals.newPassword !== vals.confirmPassword) {
      message.error('Passwords do not match.');
      return;
    }
    setPasswordSaving(true);
    try {
      await axiosClient.put('/me/password', { current_password: vals.currentPassword, new_password: vals.newPassword });
      message.success('Password updated.');
      passwordForm.resetFields();
      setNewPassword('');
    } catch (e: unknown) {
      const err = e as { response?: { status: number } };
      if (err.response?.status === 401) {
        message.error('Current password is incorrect.');
      } else {
        message.error('Failed to update password.');
      }
    } finally {
      setPasswordSaving(false);
    }
  }

  const empTabs: TabsProps['items'] = [
    {
      key: 'profile',
      label: 'Profile',
      children: (
        <div className="emp-profile-wrap">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
            <div style={{ position: 'relative', width: 72, height: 72 }}>
              <div style={{
                width: 72, height: 72, borderRadius: '50%', overflow: 'hidden',
                background: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 24, fontWeight: 600, color: '#6b7280',
              }}>
                {avatarUrl
                  ? <img src={avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : (profile.name.slice(0, 1).toUpperCase() || '?')}
              </div>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                style={{
                  position: 'absolute', bottom: -2, right: -2, width: 26, height: 26, borderRadius: '50%',
                  background: '#2563eb', border: '2px solid #fff', color: '#fff', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                }}
                title="Change photo"
              >
                <ImageIcon size={13} />
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handlePhotoChange} />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16 }}>{profile.name || 'Your name'}</div>
              {roleLabel && <Tag color="blue" style={{ marginTop: 4 }}>{roleLabel}</Tag>}
            </div>
          </div>

          <Form form={profileForm} layout="vertical">
            <Form.Item label="Full Name" name="name">
              <Input placeholder="Your name" />
            </Form.Item>
            <Form.Item label="Email">
              <Input value={profile.email} disabled />
            </Form.Item>
            <Form.Item label="Phone" name="phone">
              <Input placeholder="+1 (555) 000-0000" />
            </Form.Item>
            <Form.Item label="Address" name="address">
              <Input placeholder="123 Main St, City, ST 00000" />
            </Form.Item>
            <Form.Item label="Bio">
              <Input.TextArea
                value={bio}
                onChange={(e) => setBio(e.target.value.slice(0, 300))}
                rows={3}
                placeholder="Tell clients a bit about yourself..."
              />
              <div style={{ textAlign: 'right', fontSize: 12, color: '#9ca3af', marginTop: 4 }}>{bio.length}/300</div>
            </Form.Item>
            <Form.Item label="Specialties">
              <Select
                mode="tags"
                value={specialties}
                onChange={setSpecialties}
                placeholder="Add a specialty and press enter"
                open={false}
              />
            </Form.Item>
          </Form>
          <Button type="primary" icon={<Save size={14} />} loading={profileSaving} onClick={saveProfile}>
            {profileSaved ? 'Saved!' : 'Save Profile'}
          </Button>
        </div>
      ),
    },
    {
      key: 'security',
      label: 'Security',
      children: (
        <div className="emp-profile-wrap">
          <Form form={passwordForm} layout="vertical">
            <Form.Item label="Current Password" name="currentPassword" rules={[{ required: true }]}>
              <Input.Password />
            </Form.Item>
            <Form.Item label="New Password" name="newPassword" rules={[{ required: true, min: 8 }]}>
              <Input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  passwordForm.setFieldValue('newPassword', e.target.value);
                }}
                suffix={
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#9ca3af', display: 'flex' }}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                }
              />
              {newPassword && (
                <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} style={{
                      height: 4, flex: 1, borderRadius: 2,
                      background: i < strength.score ? strength.color : '#e5e7eb',
                    }} />
                  ))}
                  <span style={{ fontSize: 12, color: strength.color, marginLeft: 6, whiteSpace: 'nowrap' }}>{strength.label}</span>
                </div>
              )}
            </Form.Item>
            <Form.Item label="Confirm New Password" name="confirmPassword" rules={[{ required: true }]}>
              <Input.Password />
            </Form.Item>
          </Form>
          <Button type="primary" loading={passwordSaving} onClick={changePassword}>Update Password</Button>
        </div>
      ),
    },
    {
      key: 'time-clock',
      label: 'Time Clock',
      children: (
        <div className="emp-profile-wrap">
          <div style={{
            borderRadius: 12, padding: 16, marginBottom: 20,
            border: `2px solid ${clockStatus ? '#6ee7b7' : '#e5e7eb'}`,
            background: clockStatus ? '#ecfdf5' : '#fafafa',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 14, color: clockStatus ? '#047857' : '#6b7280' }}>
                <span style={{ width: 9, height: 9, borderRadius: '50%', background: clockStatus ? '#10b981' : '#d1d5db' }} />
                {clockStatus ? 'Currently Clocked In' : 'Clocked Out'}
              </div>
              {clockStatus && (
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                  Since {new Date(clockStatus.clock_in).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                </div>
              )}
            </div>
            {clockStatus ? (
              <Space>
                <InputNumber min={0} max={120} value={breakMinutes} onChange={v => setBreakMinutes(v ?? 0)} addonAfter="min break" style={{ width: 150 }} />
                <Button danger loading={clockLoading} onClick={clockOut}>Clock Out</Button>
              </Space>
            ) : (
              <Button type="primary" loading={clockLoading} onClick={clockIn}>Clock In</Button>
            )}
          </div>

          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Recent Entries</h3>
          {clockEntries.length === 0 ? (
            <p style={{ fontSize: 13, color: '#9ca3af', fontStyle: 'italic' }}>No clock entries yet.</p>
          ) : (
            <Space direction="vertical" size={6} style={{ width: '100%' }}>
              {clockEntries.slice(0, 14).map(e => (
                <div key={e.entry_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', border: '1px solid #f0f0f0', borderRadius: 8, fontSize: 13 }}>
                  <span>{new Date(e.clock_in).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                  <span style={{ color: '#6b7280' }}>
                    {new Date(e.clock_in).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    {' – '}
                    {e.clock_out ? new Date(e.clock_out).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : 'now'}
                  </span>
                  <span style={{ fontWeight: 600 }}>{netHours(e)}{e.clock_out ? ' hrs' : ''}</span>
                  <Tag color={e.status === 'approved' ? 'success' : e.status === 'pending' ? 'warning' : 'processing'}>{e.status}</Tag>
                </div>
              ))}
            </Space>
          )}
        </div>
      ),
    },
    {
      key: 'notifications',
      disabled: true,
      label: comingSoonLabel('Notifications'),
      children: (
        <div className="emp-profile-wrap">
          {[
            { key: 'email_notifications' as const, label: 'Email Notifications' },
            { key: 'sms_notifications' as const, label: 'SMS Notifications' },
            { key: 'appointment_reminders' as const, label: 'Appointment Reminders' },
            { key: 'new_booking_alerts' as const, label: 'New Booking Alerts' },
          ].map((item) => (
            <div key={item.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
              <span>{item.label}</span>
              <Switch
                checked={notifPrefs[item.key]}
                onChange={(checked) => setNotifPrefs((p) => ({ ...p, [item.key]: checked }))}
              />
            </div>
          ))}
          <Button type="primary" onClick={saveNotificationPrefs} loading={notifSaving} style={{ marginTop: 16 }}>
            {notifSaved ? 'Saved!' : 'Save Preferences'}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
        <h2 className="tab-section-title" style={{ margin: 0 }}>My Settings</h2>
      </div>
      <Tabs items={empTabs} />
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────
export default function AdminSettingsPage() {
  const auth = useAppSelector(s => s.auth);
  const isOwner = auth.role === 'admin';

  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const [activeOwnerTab, setActiveOwnerTab] = useState(requestedTab && requestedTab !== 'notifications' ? requestedTab : 'services');
  useEffect(() => {
    setActiveOwnerTab(requestedTab || 'services');
  }, [requestedTab]);

  const [services, setServices] = useState<Service[]>([]);
  const [barbers, setBarbers] = useState<Barber[]>([]);

  useEffect(() => {
    axiosClient.get('/settings/services').then(res => {
      const data: { service_id: string; name: string; price: number; duration_min: number; add_ons?: { addon_id: string; name: string; price: number }[] }[] = res.data ?? [];
      setServices(data.map(s => ({
        id: s.service_id,
        name: s.name,
        defaultPrice: s.price,
        duration: s.duration_min,
        addOns: (s.add_ons ?? []).map(a => ({ id: a.addon_id, name: a.name, price: a.price })),
      })));
    }).catch(() => {
      setServices(INITIAL_SERVICES);
    });
    axiosClient.get('/calendar/barbers', { params: { bookable_online: 1 } }).then(res => setBarbers(res.data ?? [])).catch(() => {});
  }, []);

  const ownerTabs: TabsProps['items'] = [
    { key: 'services',      label: 'Services & Add-Ons',     children: <ServicesTab services={services} setServices={setServices} /> },
    { key: 'staff',         label: 'Staff Management',       children: <StaffManagementTab services={services} /> },
    { key: 'hours',         label: 'Business Hours',         children: <BusinessHoursTab /> },
    { key: 'cashDrawer',    label: 'Cash Drawer',            children: <CashDrawerTab /> },
    { key: 'scheduling',    label: 'Scheduling Page',        children: <SchedulingPageTab barbers={barbers} /> },
    { key: 'waitlist',      label: 'Waitlist',               children: <WaitlistTab /> },
    { key: 'bookingProtection', label: 'Booking Protection', children: <BookingProtectionTab /> },
    // Nothing sends confirmations/reminders yet, so these settings would have no effect.
    { key: 'notifications', label: comingSoonLabel('Client Notifications'), disabled: true, children: <ClientNotificationsTab /> },
    { key: 'myProfile',     label: 'My Profile',             children: <EmployeeProfileView /> },
  ];

  return (
    <div className="admin-settings-page">
      <div className="settings-main">
        <h1 className="settings-page-title">Settings</h1>
        {isOwner
          ? <Tabs items={ownerTabs} size="large" activeKey={activeOwnerTab} onChange={setActiveOwnerTab} />
          : <EmployeeProfileView />
        }
      </div>
    </div>
  );
}
